import {
  JsonToBoolean,
  JsonToDouble,
  JsonToInt32,
  JsonToInt64,
  JsonToString,
  JsonToList,
  ArrayToJson,
  JsonToMap,
  MapToJson,
} from '../Helper/Converter';
export class StringIntPair {
  get descriptor() {
    return StringIntPair.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasKey = false;
    this._key = null;
    this._hasValue = false;
    this._value = null;
  }
  isEmpty() {
    if (this._hasKey) return false;
    if (this._hasValue) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof StringIntPair) {
      return value;
    }
    return StringIntPair.fromJson(value);
  }
  static fromJson(json) {
    const result = new StringIntPair();
    if (json.key != null) {
      result._hasKey = true;
      result._key = JsonToString.fromJson(json.key);
    }
    if (json.value != null) {
      result._hasValue = true;
      result._value = JsonToInt32.fromJson(json.value);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasKey) {
      result.key = JsonToString.toJson(this._key);
    }
    if (this._hasValue) {
      result.value = JsonToInt32.toJson(this._value);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get key() {
    return this._hasKey ? this._key : JsonToString.default;
  }
  get hasKey() {
    return this._hasKey;
  }
  setKey(path, value) {
    return {type: 'SET', path: path.concat(['key']), value};
  }
  deleteKey(path) {
    return {type: 'DELETE', path: path.concat(['key'])};
  }
  /**
   * @returns {number}
   */
  get value() {
    return this._hasValue ? this._value : JsonToInt32.default;
  }
  get hasValue() {
    return this._hasValue;
  }
  setValue(path, value) {
    return {type: 'SET', path: path.concat(['value']), value};
  }
  deleteValue(path) {
    return {type: 'DELETE', path: path.concat(['value'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "key": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new StringIntPair(this);
              copy._hasKey = true;
              copy._key = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasKey) {
                return this;
              }
              const copy = new StringIntPair(this);
              copy._hasKey = false;
              copy._key = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "value": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new StringIntPair(this);
              copy._hasValue = true;
              copy._value = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasValue) {
                return this;
              }
              const copy = new StringIntPair(this);
              copy._hasValue = false;
              copy._value = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {StringIntPair} other
   * @returns {StringIntPair}
   */
  merge(other) {
    const copy = new StringIntPair(this);
    if (other._hasKey) {
      copy._hasKey = true;
      copy._key = other._key;
    }
    if (other._hasValue) {
      copy._hasValue = true;
      copy._value = other._value;
    }
    return copy;
  }
}
StringIntPair.default = new StringIntPair();
class StringIntPair_Builder {
  constructor(value) {
    this._value = new StringIntPair(value);
  }
  build() {
    return new StringIntPair(this._value);
  }
  setKey(value) {
    this._value._hasKey = true;
    this._value._key = value;
    return this;
  }
  deleteKey() {
    this._value._hasKey = false;
    this._value._key = null;
    return this;
  }
  setValue(value) {
    this._value._hasValue = true;
    this._value._value = value;
    return this;
  }
  deleteValue() {
    this._value._hasValue = false;
    this._value._value = null;
    return this;
  }
}
StringIntPair.Builder = StringIntPair_Builder;
class StringIntPair_WithParent {
  /**
   * @param {StringIntPair} value
   * @param {StringIntPair_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return StringIntPair.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasKey() {
    if (this._cachedHasKey === undefined) {
      if (this._value.hasKey) {
        this._cachedHasKey = true;
      } else {
        this._cachedHasKey = this._parent ? this._parent.hasKey : false;
      }
    }
    return this._cachedHasKey;
  }
  get key() {
    if (this._cachedKey === undefined) {
      if (this._value.hasKey) {
        this._cachedKey = this._value.key;
      } else {
        this._cachedKey = this._parent ? this._parent.key : this._value.key;
      }
    }
    return this._cachedKey;
  }
  get hasValue() {
    if (this._cachedHasValue === undefined) {
      if (this._value.hasValue) {
        this._cachedHasValue = true;
      } else {
        this._cachedHasValue = this._parent ? this._parent.hasValue : false;
      }
    }
    return this._cachedHasValue;
  }
  get value() {
    if (this._cachedValue === undefined) {
      if (this._value.hasValue) {
        this._cachedValue = this._value.value;
      } else {
        this._cachedValue = this._parent ? this._parent.value : this._value.value;
      }
    }
    return this._cachedValue;
  }
  update(current, parent) {
    const result = new StringIntPair_WithParent(current, parent);
    return result;
  }
}
StringIntPair_WithParent.default = new StringIntPair_WithParent(StringIntPair.default, null);
StringIntPair.WithParent = StringIntPair_WithParent;
class StringIntPair_Descriptor {
  get key() {
    if (!this._key) {
      this._key = {
        name: "key",
        Name: "Key",
        number: 1,
        protoType: 9,
        protoName: "key",
        type: JsonToString,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._key;
  }
  get value() {
    if (!this._value) {
      this._value = {
        name: "value",
        Name: "Value",
        number: 2,
        protoType: 5,
        protoName: "value",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._value;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.key,
        this.value,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForKey",
        Name: "DummyOneofForKey",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForValue",
        Name: "DummyOneofForValue",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
StringIntPair.descriptor = new StringIntPair_Descriptor();
export class InventoryItemKeyValuePair {
  get descriptor() {
    return InventoryItemKeyValuePair.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasKey = false;
    this._key = null;
    this._hasValue = false;
    this._value = null;
  }
  isEmpty() {
    if (this._hasKey) return false;
    if (this._hasValue) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof InventoryItemKeyValuePair) {
      return value;
    }
    return InventoryItemKeyValuePair.fromJson(value);
  }
  static fromJson(json) {
    const result = new InventoryItemKeyValuePair();
    if (json.key != null) {
      result._hasKey = true;
      result._key = JsonToString.fromJson(json.key);
    }
    if (json.value != null) {
      result._hasValue = true;
      result._value = JsonToInt32.fromJson(json.value);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasKey) {
      result.key = JsonToString.toJson(this._key);
    }
    if (this._hasValue) {
      result.value = JsonToInt32.toJson(this._value);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get key() {
    return this._hasKey ? this._key : JsonToString.default;
  }
  get hasKey() {
    return this._hasKey;
  }
  setKey(path, value) {
    return {type: 'SET', path: path.concat(['key']), value};
  }
  deleteKey(path) {
    return {type: 'DELETE', path: path.concat(['key'])};
  }
  /**
   * @returns {number}
   */
  get value() {
    return this._hasValue ? this._value : JsonToInt32.default;
  }
  get hasValue() {
    return this._hasValue;
  }
  setValue(path, value) {
    return {type: 'SET', path: path.concat(['value']), value};
  }
  deleteValue(path) {
    return {type: 'DELETE', path: path.concat(['value'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "key": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new InventoryItemKeyValuePair(this);
              copy._hasKey = true;
              copy._key = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasKey) {
                return this;
              }
              const copy = new InventoryItemKeyValuePair(this);
              copy._hasKey = false;
              copy._key = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "value": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new InventoryItemKeyValuePair(this);
              copy._hasValue = true;
              copy._value = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasValue) {
                return this;
              }
              const copy = new InventoryItemKeyValuePair(this);
              copy._hasValue = false;
              copy._value = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {InventoryItemKeyValuePair} other
   * @returns {InventoryItemKeyValuePair}
   */
  merge(other) {
    const copy = new InventoryItemKeyValuePair(this);
    if (other._hasKey) {
      copy._hasKey = true;
      copy._key = other._key;
    }
    if (other._hasValue) {
      copy._hasValue = true;
      copy._value = other._value;
    }
    return copy;
  }
}
InventoryItemKeyValuePair.default = new InventoryItemKeyValuePair();
class InventoryItemKeyValuePair_Builder {
  constructor(value) {
    this._value = new InventoryItemKeyValuePair(value);
  }
  build() {
    return new InventoryItemKeyValuePair(this._value);
  }
  setKey(value) {
    this._value._hasKey = true;
    this._value._key = value;
    return this;
  }
  deleteKey() {
    this._value._hasKey = false;
    this._value._key = null;
    return this;
  }
  setValue(value) {
    this._value._hasValue = true;
    this._value._value = value;
    return this;
  }
  deleteValue() {
    this._value._hasValue = false;
    this._value._value = null;
    return this;
  }
}
InventoryItemKeyValuePair.Builder = InventoryItemKeyValuePair_Builder;
class InventoryItemKeyValuePair_WithParent {
  /**
   * @param {InventoryItemKeyValuePair} value
   * @param {InventoryItemKeyValuePair_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return InventoryItemKeyValuePair.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasKey() {
    if (this._cachedHasKey === undefined) {
      if (this._value.hasKey) {
        this._cachedHasKey = true;
      } else {
        this._cachedHasKey = this._parent ? this._parent.hasKey : false;
      }
    }
    return this._cachedHasKey;
  }
  get key() {
    if (this._cachedKey === undefined) {
      if (this._value.hasKey) {
        this._cachedKey = this._value.key;
      } else {
        this._cachedKey = this._parent ? this._parent.key : this._value.key;
      }
    }
    return this._cachedKey;
  }
  get hasValue() {
    if (this._cachedHasValue === undefined) {
      if (this._value.hasValue) {
        this._cachedHasValue = true;
      } else {
        this._cachedHasValue = this._parent ? this._parent.hasValue : false;
      }
    }
    return this._cachedHasValue;
  }
  get value() {
    if (this._cachedValue === undefined) {
      if (this._value.hasValue) {
        this._cachedValue = this._value.value;
      } else {
        this._cachedValue = this._parent ? this._parent.value : this._value.value;
      }
    }
    return this._cachedValue;
  }
  update(current, parent) {
    const result = new InventoryItemKeyValuePair_WithParent(current, parent);
    return result;
  }
}
InventoryItemKeyValuePair_WithParent.default = new InventoryItemKeyValuePair_WithParent(InventoryItemKeyValuePair.default, null);
InventoryItemKeyValuePair.WithParent = InventoryItemKeyValuePair_WithParent;
class InventoryItemKeyValuePair_Descriptor {
  get key() {
    if (!this._key) {
      this._key = {
        name: "key",
        Name: "Key",
        number: 1,
        protoType: 9,
        protoName: "key",
        type: JsonToString,
        options: {
          valueDropdown: "RewardItemKeyList",
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._key;
  }
  get value() {
    if (!this._value) {
      this._value = {
        name: "value",
        Name: "Value",
        number: 2,
        protoType: 5,
        protoName: "value",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._value;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.key,
        this.value,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForKey",
        Name: "DummyOneofForKey",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForValue",
        Name: "DummyOneofForValue",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
InventoryItemKeyValuePair.descriptor = new InventoryItemKeyValuePair_Descriptor();
export class StringStringPair {
  get descriptor() {
    return StringStringPair.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasKey = false;
    this._key = null;
    this._hasValue = false;
    this._value = null;
  }
  isEmpty() {
    if (this._hasKey) return false;
    if (this._hasValue) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof StringStringPair) {
      return value;
    }
    return StringStringPair.fromJson(value);
  }
  static fromJson(json) {
    const result = new StringStringPair();
    if (json.key != null) {
      result._hasKey = true;
      result._key = JsonToString.fromJson(json.key);
    }
    if (json.value != null) {
      result._hasValue = true;
      result._value = JsonToString.fromJson(json.value);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasKey) {
      result.key = JsonToString.toJson(this._key);
    }
    if (this._hasValue) {
      result.value = JsonToString.toJson(this._value);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get key() {
    return this._hasKey ? this._key : JsonToString.default;
  }
  get hasKey() {
    return this._hasKey;
  }
  setKey(path, value) {
    return {type: 'SET', path: path.concat(['key']), value};
  }
  deleteKey(path) {
    return {type: 'DELETE', path: path.concat(['key'])};
  }
  /**
   * @returns {string}
   */
  get value() {
    return this._hasValue ? this._value : JsonToString.default;
  }
  get hasValue() {
    return this._hasValue;
  }
  setValue(path, value) {
    return {type: 'SET', path: path.concat(['value']), value};
  }
  deleteValue(path) {
    return {type: 'DELETE', path: path.concat(['value'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "key": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new StringStringPair(this);
              copy._hasKey = true;
              copy._key = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasKey) {
                return this;
              }
              const copy = new StringStringPair(this);
              copy._hasKey = false;
              copy._key = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "value": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new StringStringPair(this);
              copy._hasValue = true;
              copy._value = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasValue) {
                return this;
              }
              const copy = new StringStringPair(this);
              copy._hasValue = false;
              copy._value = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {StringStringPair} other
   * @returns {StringStringPair}
   */
  merge(other) {
    const copy = new StringStringPair(this);
    if (other._hasKey) {
      copy._hasKey = true;
      copy._key = other._key;
    }
    if (other._hasValue) {
      copy._hasValue = true;
      copy._value = other._value;
    }
    return copy;
  }
}
StringStringPair.default = new StringStringPair();
class StringStringPair_Builder {
  constructor(value) {
    this._value = new StringStringPair(value);
  }
  build() {
    return new StringStringPair(this._value);
  }
  setKey(value) {
    this._value._hasKey = true;
    this._value._key = value;
    return this;
  }
  deleteKey() {
    this._value._hasKey = false;
    this._value._key = null;
    return this;
  }
  setValue(value) {
    this._value._hasValue = true;
    this._value._value = value;
    return this;
  }
  deleteValue() {
    this._value._hasValue = false;
    this._value._value = null;
    return this;
  }
}
StringStringPair.Builder = StringStringPair_Builder;
class StringStringPair_WithParent {
  /**
   * @param {StringStringPair} value
   * @param {StringStringPair_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return StringStringPair.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasKey() {
    if (this._cachedHasKey === undefined) {
      if (this._value.hasKey) {
        this._cachedHasKey = true;
      } else {
        this._cachedHasKey = this._parent ? this._parent.hasKey : false;
      }
    }
    return this._cachedHasKey;
  }
  get key() {
    if (this._cachedKey === undefined) {
      if (this._value.hasKey) {
        this._cachedKey = this._value.key;
      } else {
        this._cachedKey = this._parent ? this._parent.key : this._value.key;
      }
    }
    return this._cachedKey;
  }
  get hasValue() {
    if (this._cachedHasValue === undefined) {
      if (this._value.hasValue) {
        this._cachedHasValue = true;
      } else {
        this._cachedHasValue = this._parent ? this._parent.hasValue : false;
      }
    }
    return this._cachedHasValue;
  }
  get value() {
    if (this._cachedValue === undefined) {
      if (this._value.hasValue) {
        this._cachedValue = this._value.value;
      } else {
        this._cachedValue = this._parent ? this._parent.value : this._value.value;
      }
    }
    return this._cachedValue;
  }
  update(current, parent) {
    const result = new StringStringPair_WithParent(current, parent);
    return result;
  }
}
StringStringPair_WithParent.default = new StringStringPair_WithParent(StringStringPair.default, null);
StringStringPair.WithParent = StringStringPair_WithParent;
class StringStringPair_Descriptor {
  get key() {
    if (!this._key) {
      this._key = {
        name: "key",
        Name: "Key",
        number: 1,
        protoType: 9,
        protoName: "key",
        type: JsonToString,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._key;
  }
  get value() {
    if (!this._value) {
      this._value = {
        name: "value",
        Name: "Value",
        number: 2,
        protoType: 9,
        protoName: "value",
        type: JsonToString,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._value;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.key,
        this.value,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForKey",
        Name: "DummyOneofForKey",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForValue",
        Name: "DummyOneofForValue",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
StringStringPair.descriptor = new StringStringPair_Descriptor();
export class IntRange {
  get descriptor() {
    return IntRange.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasMin = false;
    this._min = null;
    this._hasMax = false;
    this._max = null;
  }
  isEmpty() {
    if (this._hasMin) return false;
    if (this._hasMax) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof IntRange) {
      return value;
    }
    return IntRange.fromJson(value);
  }
  static fromJson(json) {
    const result = new IntRange();
    if (json.min != null) {
      result._hasMin = true;
      result._min = JsonToInt32.fromJson(json.min);
    }
    if (json.max != null) {
      result._hasMax = true;
      result._max = JsonToInt32.fromJson(json.max);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasMin) {
      result.min = JsonToInt32.toJson(this._min);
    }
    if (this._hasMax) {
      result.max = JsonToInt32.toJson(this._max);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get min() {
    return this._hasMin ? this._min : JsonToInt32.default;
  }
  get hasMin() {
    return this._hasMin;
  }
  setMin(path, value) {
    return {type: 'SET', path: path.concat(['min']), value};
  }
  deleteMin(path) {
    return {type: 'DELETE', path: path.concat(['min'])};
  }
  /**
   * @returns {number}
   */
  get max() {
    return this._hasMax ? this._max : JsonToInt32.default;
  }
  get hasMax() {
    return this._hasMax;
  }
  setMax(path, value) {
    return {type: 'SET', path: path.concat(['max']), value};
  }
  deleteMax(path) {
    return {type: 'DELETE', path: path.concat(['max'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "min": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new IntRange(this);
              copy._hasMin = true;
              copy._min = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasMin) {
                return this;
              }
              const copy = new IntRange(this);
              copy._hasMin = false;
              copy._min = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "max": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new IntRange(this);
              copy._hasMax = true;
              copy._max = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasMax) {
                return this;
              }
              const copy = new IntRange(this);
              copy._hasMax = false;
              copy._max = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {IntRange} other
   * @returns {IntRange}
   */
  merge(other) {
    const copy = new IntRange(this);
    if (other._hasMin) {
      copy._hasMin = true;
      copy._min = other._min;
    }
    if (other._hasMax) {
      copy._hasMax = true;
      copy._max = other._max;
    }
    return copy;
  }
}
IntRange.default = new IntRange();
class IntRange_Builder {
  constructor(value) {
    this._value = new IntRange(value);
  }
  build() {
    return new IntRange(this._value);
  }
  setMin(value) {
    this._value._hasMin = true;
    this._value._min = value;
    return this;
  }
  deleteMin() {
    this._value._hasMin = false;
    this._value._min = null;
    return this;
  }
  setMax(value) {
    this._value._hasMax = true;
    this._value._max = value;
    return this;
  }
  deleteMax() {
    this._value._hasMax = false;
    this._value._max = null;
    return this;
  }
}
IntRange.Builder = IntRange_Builder;
class IntRange_WithParent {
  /**
   * @param {IntRange} value
   * @param {IntRange_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return IntRange.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasMin() {
    if (this._cachedHasMin === undefined) {
      if (this._value.hasMin) {
        this._cachedHasMin = true;
      } else {
        this._cachedHasMin = this._parent ? this._parent.hasMin : false;
      }
    }
    return this._cachedHasMin;
  }
  get min() {
    if (this._cachedMin === undefined) {
      if (this._value.hasMin) {
        this._cachedMin = this._value.min;
      } else {
        this._cachedMin = this._parent ? this._parent.min : this._value.min;
      }
    }
    return this._cachedMin;
  }
  get hasMax() {
    if (this._cachedHasMax === undefined) {
      if (this._value.hasMax) {
        this._cachedHasMax = true;
      } else {
        this._cachedHasMax = this._parent ? this._parent.hasMax : false;
      }
    }
    return this._cachedHasMax;
  }
  get max() {
    if (this._cachedMax === undefined) {
      if (this._value.hasMax) {
        this._cachedMax = this._value.max;
      } else {
        this._cachedMax = this._parent ? this._parent.max : this._value.max;
      }
    }
    return this._cachedMax;
  }
  update(current, parent) {
    const result = new IntRange_WithParent(current, parent);
    return result;
  }
}
IntRange_WithParent.default = new IntRange_WithParent(IntRange.default, null);
IntRange.WithParent = IntRange_WithParent;
class IntRange_Descriptor {
  get min() {
    if (!this._min) {
      this._min = {
        name: "min",
        Name: "Min",
        number: 1,
        protoType: 5,
        protoName: "min",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._min;
  }
  get max() {
    if (!this._max) {
      this._max = {
        name: "max",
        Name: "Max",
        number: 2,
        protoType: 5,
        protoName: "max",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._max;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.min,
        this.max,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForMin",
        Name: "DummyOneofForMin",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForMax",
        Name: "DummyOneofForMax",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
IntRange.descriptor = new IntRange_Descriptor();
export class EndGameStats {
  get descriptor() {
    return EndGameStats.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasMaxGold = false;
    this._maxGold = null;
    this._hasGoldEarned = false;
    this._goldEarned = null;
    this._hasGoldSpent = false;
    this._goldSpent = null;
  }
  isEmpty() {
    if (this._hasMaxGold) return false;
    if (this._hasGoldEarned) return false;
    if (this._hasGoldSpent) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof EndGameStats) {
      return value;
    }
    return EndGameStats.fromJson(value);
  }
  static fromJson(json) {
    const result = new EndGameStats();
    if (json.maxGold != null) {
      result._hasMaxGold = true;
      result._maxGold = JsonToInt32.fromJson(json.maxGold);
    }
    if (json.goldEarned != null) {
      result._hasGoldEarned = true;
      result._goldEarned = JsonToInt32.fromJson(json.goldEarned);
    }
    if (json.goldSpent != null) {
      result._hasGoldSpent = true;
      result._goldSpent = JsonToInt32.fromJson(json.goldSpent);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasMaxGold) {
      result.maxGold = JsonToInt32.toJson(this._maxGold);
    }
    if (this._hasGoldEarned) {
      result.goldEarned = JsonToInt32.toJson(this._goldEarned);
    }
    if (this._hasGoldSpent) {
      result.goldSpent = JsonToInt32.toJson(this._goldSpent);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get maxGold() {
    return this._hasMaxGold ? this._maxGold : JsonToInt32.default;
  }
  get hasMaxGold() {
    return this._hasMaxGold;
  }
  setMaxGold(path, value) {
    return {type: 'SET', path: path.concat(['maxGold']), value};
  }
  deleteMaxGold(path) {
    return {type: 'DELETE', path: path.concat(['maxGold'])};
  }
  /**
   * @returns {number}
   */
  get goldEarned() {
    return this._hasGoldEarned ? this._goldEarned : JsonToInt32.default;
  }
  get hasGoldEarned() {
    return this._hasGoldEarned;
  }
  setGoldEarned(path, value) {
    return {type: 'SET', path: path.concat(['goldEarned']), value};
  }
  deleteGoldEarned(path) {
    return {type: 'DELETE', path: path.concat(['goldEarned'])};
  }
  /**
   * @returns {number}
   */
  get goldSpent() {
    return this._hasGoldSpent ? this._goldSpent : JsonToInt32.default;
  }
  get hasGoldSpent() {
    return this._hasGoldSpent;
  }
  setGoldSpent(path, value) {
    return {type: 'SET', path: path.concat(['goldSpent']), value};
  }
  deleteGoldSpent(path) {
    return {type: 'DELETE', path: path.concat(['goldSpent'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "maxGold": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new EndGameStats(this);
              copy._hasMaxGold = true;
              copy._maxGold = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasMaxGold) {
                return this;
              }
              const copy = new EndGameStats(this);
              copy._hasMaxGold = false;
              copy._maxGold = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "goldEarned": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new EndGameStats(this);
              copy._hasGoldEarned = true;
              copy._goldEarned = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasGoldEarned) {
                return this;
              }
              const copy = new EndGameStats(this);
              copy._hasGoldEarned = false;
              copy._goldEarned = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "goldSpent": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new EndGameStats(this);
              copy._hasGoldSpent = true;
              copy._goldSpent = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasGoldSpent) {
                return this;
              }
              const copy = new EndGameStats(this);
              copy._hasGoldSpent = false;
              copy._goldSpent = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {EndGameStats} other
   * @returns {EndGameStats}
   */
  merge(other) {
    const copy = new EndGameStats(this);
    if (other._hasMaxGold) {
      copy._hasMaxGold = true;
      copy._maxGold = other._maxGold;
    }
    if (other._hasGoldEarned) {
      copy._hasGoldEarned = true;
      copy._goldEarned = other._goldEarned;
    }
    if (other._hasGoldSpent) {
      copy._hasGoldSpent = true;
      copy._goldSpent = other._goldSpent;
    }
    return copy;
  }
}
EndGameStats.default = new EndGameStats();
class EndGameStats_Builder {
  constructor(value) {
    this._value = new EndGameStats(value);
  }
  build() {
    return new EndGameStats(this._value);
  }
  setMaxGold(value) {
    this._value._hasMaxGold = true;
    this._value._maxGold = value;
    return this;
  }
  deleteMaxGold() {
    this._value._hasMaxGold = false;
    this._value._maxGold = null;
    return this;
  }
  setGoldEarned(value) {
    this._value._hasGoldEarned = true;
    this._value._goldEarned = value;
    return this;
  }
  deleteGoldEarned() {
    this._value._hasGoldEarned = false;
    this._value._goldEarned = null;
    return this;
  }
  setGoldSpent(value) {
    this._value._hasGoldSpent = true;
    this._value._goldSpent = value;
    return this;
  }
  deleteGoldSpent() {
    this._value._hasGoldSpent = false;
    this._value._goldSpent = null;
    return this;
  }
}
EndGameStats.Builder = EndGameStats_Builder;
class EndGameStats_WithParent {
  /**
   * @param {EndGameStats} value
   * @param {EndGameStats_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return EndGameStats.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasMaxGold() {
    if (this._cachedHasMaxGold === undefined) {
      if (this._value.hasMaxGold) {
        this._cachedHasMaxGold = true;
      } else {
        this._cachedHasMaxGold = this._parent ? this._parent.hasMaxGold : false;
      }
    }
    return this._cachedHasMaxGold;
  }
  get maxGold() {
    if (this._cachedMaxGold === undefined) {
      if (this._value.hasMaxGold) {
        this._cachedMaxGold = this._value.maxGold;
      } else {
        this._cachedMaxGold = this._parent ? this._parent.maxGold : this._value.maxGold;
      }
    }
    return this._cachedMaxGold;
  }
  get hasGoldEarned() {
    if (this._cachedHasGoldEarned === undefined) {
      if (this._value.hasGoldEarned) {
        this._cachedHasGoldEarned = true;
      } else {
        this._cachedHasGoldEarned = this._parent ? this._parent.hasGoldEarned : false;
      }
    }
    return this._cachedHasGoldEarned;
  }
  get goldEarned() {
    if (this._cachedGoldEarned === undefined) {
      if (this._value.hasGoldEarned) {
        this._cachedGoldEarned = this._value.goldEarned;
      } else {
        this._cachedGoldEarned = this._parent ? this._parent.goldEarned : this._value.goldEarned;
      }
    }
    return this._cachedGoldEarned;
  }
  get hasGoldSpent() {
    if (this._cachedHasGoldSpent === undefined) {
      if (this._value.hasGoldSpent) {
        this._cachedHasGoldSpent = true;
      } else {
        this._cachedHasGoldSpent = this._parent ? this._parent.hasGoldSpent : false;
      }
    }
    return this._cachedHasGoldSpent;
  }
  get goldSpent() {
    if (this._cachedGoldSpent === undefined) {
      if (this._value.hasGoldSpent) {
        this._cachedGoldSpent = this._value.goldSpent;
      } else {
        this._cachedGoldSpent = this._parent ? this._parent.goldSpent : this._value.goldSpent;
      }
    }
    return this._cachedGoldSpent;
  }
  update(current, parent) {
    const result = new EndGameStats_WithParent(current, parent);
    return result;
  }
}
EndGameStats_WithParent.default = new EndGameStats_WithParent(EndGameStats.default, null);
EndGameStats.WithParent = EndGameStats_WithParent;
class EndGameStats_Descriptor {
  get maxGold() {
    if (!this._maxGold) {
      this._maxGold = {
        name: "maxGold",
        Name: "MaxGold",
        number: 1,
        protoType: 5,
        protoName: "max_gold",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._maxGold;
  }
  get goldEarned() {
    if (!this._goldEarned) {
      this._goldEarned = {
        name: "goldEarned",
        Name: "GoldEarned",
        number: 2,
        protoType: 5,
        protoName: "gold_earned",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._goldEarned;
  }
  get goldSpent() {
    if (!this._goldSpent) {
      this._goldSpent = {
        name: "goldSpent",
        Name: "GoldSpent",
        number: 3,
        protoType: 5,
        protoName: "gold_spent",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._goldSpent;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.maxGold,
        this.goldEarned,
        this.goldSpent,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForMaxGold",
        Name: "DummyOneofForMaxGold",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForGoldEarned",
        Name: "DummyOneofForGoldEarned",
        fields: [
          this.fields[1],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForGoldSpent",
        Name: "DummyOneofForGoldSpent",
        fields: [
          this.fields[2],
        ],
      });
    }
    return this._oneofs;
  }
}
EndGameStats.descriptor = new EndGameStats_Descriptor();
class PageNavigationEntry_HeroCardPage_Tab {
  static fromJson(json) {
    if (typeof json === "string") {
      switch (json) {
        case "SKILL": return 0;
        case "PROMOTE": return 1;
        case "AWAKEN": return 2;
        default: return 0;
      }
    }
    return json;
  }
  static toJson(value) {
    return value;
  }
  static fromAny(value) {
    return PageNavigationEntry_HeroCardPage_Tab.fromJson(value);
  }
  static fromReact(e) {
    return PageNavigationEntry_HeroCardPage_Tab.fromJson(e);
  }
}
PageNavigationEntry_HeroCardPage_Tab.SKILL = 0;
PageNavigationEntry_HeroCardPage_Tab.PROMOTE = 1;
PageNavigationEntry_HeroCardPage_Tab.AWAKEN = 2;
PageNavigationEntry_HeroCardPage_Tab.default = 0;
PageNavigationEntry_HeroCardPage_Tab.values = {
  SKILL: 0,
  PROMOTE: 1,
  AWAKEN: 2,
};
PageNavigationEntry_HeroCardPage_Tab.names = {
  0: "SKILL",
  1: "PROMOTE",
  2: "AWAKEN",
};
class PageNavigationEntry_HeroCardPage {
  get descriptor() {
    return PageNavigationEntry_HeroCardPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasCardName = false;
    this._cardName = null;
    this._hasTab = false;
    this._tab = null;
  }
  isEmpty() {
    if (this._hasCardName) return false;
    if (this._hasTab) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_HeroCardPage) {
      return value;
    }
    return PageNavigationEntry_HeroCardPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_HeroCardPage();
    if (json.cardName != null) {
      result._hasCardName = true;
      result._cardName = JsonToString.fromJson(json.cardName);
    }
    if (json.tab != null) {
      result._hasTab = true;
      result._tab = PageNavigationEntry_HeroCardPage_Tab.fromJson(json.tab);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasCardName) {
      result.cardName = JsonToString.toJson(this._cardName);
    }
    if (this._hasTab) {
      result.tab = PageNavigationEntry_HeroCardPage_Tab.toJson(this._tab);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get cardName() {
    return this._hasCardName ? this._cardName : JsonToString.default;
  }
  get hasCardName() {
    return this._hasCardName;
  }
  setCardName(path, value) {
    return {type: 'SET', path: path.concat(['cardName']), value};
  }
  deleteCardName(path) {
    return {type: 'DELETE', path: path.concat(['cardName'])};
  }
  /**
   * @returns {number}
   */
  get tab() {
    return this._hasTab ? this._tab : PageNavigationEntry_HeroCardPage_Tab.default;
  }
  get hasTab() {
    return this._hasTab;
  }
  setTab(path, value) {
    return {type: 'SET', path: path.concat(['tab']), value};
  }
  deleteTab(path) {
    return {type: 'DELETE', path: path.concat(['tab'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "cardName": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_HeroCardPage(this);
              copy._hasCardName = true;
              copy._cardName = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasCardName) {
                return this;
              }
              const copy = new PageNavigationEntry_HeroCardPage(this);
              copy._hasCardName = false;
              copy._cardName = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "tab": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_HeroCardPage(this);
              copy._hasTab = true;
              copy._tab = PageNavigationEntry_HeroCardPage_Tab.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasTab) {
                return this;
              }
              const copy = new PageNavigationEntry_HeroCardPage(this);
              copy._hasTab = false;
              copy._tab = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_HeroCardPage} other
   * @returns {PageNavigationEntry_HeroCardPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_HeroCardPage(this);
    if (other._hasCardName) {
      copy._hasCardName = true;
      copy._cardName = other._cardName;
    }
    if (other._hasTab) {
      copy._hasTab = true;
      copy._tab = other._tab;
    }
    return copy;
  }
}
PageNavigationEntry_HeroCardPage.default = new PageNavigationEntry_HeroCardPage();
PageNavigationEntry_HeroCardPage.Tab = PageNavigationEntry_HeroCardPage_Tab;
class PageNavigationEntry_HeroCardPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_HeroCardPage(value);
  }
  build() {
    return new PageNavigationEntry_HeroCardPage(this._value);
  }
  setCardName(value) {
    this._value._hasCardName = true;
    this._value._cardName = value;
    return this;
  }
  deleteCardName() {
    this._value._hasCardName = false;
    this._value._cardName = null;
    return this;
  }
  setTab(value) {
    this._value._hasTab = true;
    this._value._tab = value;
    return this;
  }
  deleteTab() {
    this._value._hasTab = false;
    this._value._tab = null;
    return this;
  }
}
PageNavigationEntry_HeroCardPage.Builder = PageNavigationEntry_HeroCardPage_Builder;
class PageNavigationEntry_HeroCardPage_WithParent {
  /**
   * @param {PageNavigationEntry_HeroCardPage} value
   * @param {PageNavigationEntry_HeroCardPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_HeroCardPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasCardName() {
    if (this._cachedHasCardName === undefined) {
      if (this._value.hasCardName) {
        this._cachedHasCardName = true;
      } else {
        this._cachedHasCardName = this._parent ? this._parent.hasCardName : false;
      }
    }
    return this._cachedHasCardName;
  }
  get cardName() {
    if (this._cachedCardName === undefined) {
      if (this._value.hasCardName) {
        this._cachedCardName = this._value.cardName;
      } else {
        this._cachedCardName = this._parent ? this._parent.cardName : this._value.cardName;
      }
    }
    return this._cachedCardName;
  }
  get hasTab() {
    if (this._cachedHasTab === undefined) {
      if (this._value.hasTab) {
        this._cachedHasTab = true;
      } else {
        this._cachedHasTab = this._parent ? this._parent.hasTab : false;
      }
    }
    return this._cachedHasTab;
  }
  get tab() {
    if (this._cachedTab === undefined) {
      if (this._value.hasTab) {
        this._cachedTab = this._value.tab;
      } else {
        this._cachedTab = this._parent ? this._parent.tab : this._value.tab;
      }
    }
    return this._cachedTab;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_HeroCardPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_HeroCardPage_WithParent.default = new PageNavigationEntry_HeroCardPage_WithParent(PageNavigationEntry_HeroCardPage.default, null);
PageNavigationEntry_HeroCardPage.WithParent = PageNavigationEntry_HeroCardPage_WithParent;
class PageNavigationEntry_HeroCardPage_Descriptor {
  get cardName() {
    if (!this._cardName) {
      this._cardName = {
        name: "cardName",
        Name: "CardName",
        number: 1,
        protoType: 9,
        protoName: "card_name",
        type: JsonToString,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._cardName;
  }
  get tab() {
    if (!this._tab) {
      this._tab = {
        name: "tab",
        Name: "Tab",
        number: 2,
        protoType: 14,
        protoName: "tab",
        type: PageNavigationEntry_HeroCardPage_Tab,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._tab;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.cardName,
        this.tab,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForCardName",
        Name: "DummyOneofForCardName",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForTab",
        Name: "DummyOneofForTab",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_HeroCardPage.descriptor = new PageNavigationEntry_HeroCardPage_Descriptor();
class PageNavigationEntry_CranePage_SubPage {
  static fromJson(json) {
    if (typeof json === "string") {
      switch (json) {
        case "VIEW_PAGE": return 0;
        case "DROP_INFO_PAGE": return 1;
        default: return 0;
      }
    }
    return json;
  }
  static toJson(value) {
    return value;
  }
  static fromAny(value) {
    return PageNavigationEntry_CranePage_SubPage.fromJson(value);
  }
  static fromReact(e) {
    return PageNavigationEntry_CranePage_SubPage.fromJson(e);
  }
}
PageNavigationEntry_CranePage_SubPage.VIEW_PAGE = 0;
PageNavigationEntry_CranePage_SubPage.DROP_INFO_PAGE = 1;
PageNavigationEntry_CranePage_SubPage.default = 0;
PageNavigationEntry_CranePage_SubPage.values = {
  VIEW_PAGE: 0,
  DROP_INFO_PAGE: 1,
};
PageNavigationEntry_CranePage_SubPage.names = {
  0: "VIEW_PAGE",
  1: "DROP_INFO_PAGE",
};
class PageNavigationEntry_CranePage {
  get descriptor() {
    return PageNavigationEntry_CranePage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasChestGroupKey = false;
    this._chestGroupKey = null;
    this._hasSubPage = false;
    this._subPage = null;
  }
  isEmpty() {
    if (this._hasChestGroupKey) return false;
    if (this._hasSubPage) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_CranePage) {
      return value;
    }
    return PageNavigationEntry_CranePage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_CranePage();
    if (json.chestGroupKey != null) {
      result._hasChestGroupKey = true;
      result._chestGroupKey = JsonToString.fromJson(json.chestGroupKey);
    }
    if (json.subPage != null) {
      result._hasSubPage = true;
      result._subPage = PageNavigationEntry_CranePage_SubPage.fromJson(json.subPage);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasChestGroupKey) {
      result.chestGroupKey = JsonToString.toJson(this._chestGroupKey);
    }
    if (this._hasSubPage) {
      result.subPage = PageNavigationEntry_CranePage_SubPage.toJson(this._subPage);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get chestGroupKey() {
    return this._hasChestGroupKey ? this._chestGroupKey : JsonToString.default;
  }
  get hasChestGroupKey() {
    return this._hasChestGroupKey;
  }
  setChestGroupKey(path, value) {
    return {type: 'SET', path: path.concat(['chestGroupKey']), value};
  }
  deleteChestGroupKey(path) {
    return {type: 'DELETE', path: path.concat(['chestGroupKey'])};
  }
  /**
   * @returns {number}
   */
  get subPage() {
    return this._hasSubPage ? this._subPage : PageNavigationEntry_CranePage_SubPage.default;
  }
  get hasSubPage() {
    return this._hasSubPage;
  }
  setSubPage(path, value) {
    return {type: 'SET', path: path.concat(['subPage']), value};
  }
  deleteSubPage(path) {
    return {type: 'DELETE', path: path.concat(['subPage'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "chestGroupKey": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_CranePage(this);
              copy._hasChestGroupKey = true;
              copy._chestGroupKey = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasChestGroupKey) {
                return this;
              }
              const copy = new PageNavigationEntry_CranePage(this);
              copy._hasChestGroupKey = false;
              copy._chestGroupKey = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "subPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_CranePage(this);
              copy._hasSubPage = true;
              copy._subPage = PageNavigationEntry_CranePage_SubPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasSubPage) {
                return this;
              }
              const copy = new PageNavigationEntry_CranePage(this);
              copy._hasSubPage = false;
              copy._subPage = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_CranePage} other
   * @returns {PageNavigationEntry_CranePage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_CranePage(this);
    if (other._hasChestGroupKey) {
      copy._hasChestGroupKey = true;
      copy._chestGroupKey = other._chestGroupKey;
    }
    if (other._hasSubPage) {
      copy._hasSubPage = true;
      copy._subPage = other._subPage;
    }
    return copy;
  }
}
PageNavigationEntry_CranePage.default = new PageNavigationEntry_CranePage();
PageNavigationEntry_CranePage.SubPage = PageNavigationEntry_CranePage_SubPage;
class PageNavigationEntry_CranePage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_CranePage(value);
  }
  build() {
    return new PageNavigationEntry_CranePage(this._value);
  }
  setChestGroupKey(value) {
    this._value._hasChestGroupKey = true;
    this._value._chestGroupKey = value;
    return this;
  }
  deleteChestGroupKey() {
    this._value._hasChestGroupKey = false;
    this._value._chestGroupKey = null;
    return this;
  }
  setSubPage(value) {
    this._value._hasSubPage = true;
    this._value._subPage = value;
    return this;
  }
  deleteSubPage() {
    this._value._hasSubPage = false;
    this._value._subPage = null;
    return this;
  }
}
PageNavigationEntry_CranePage.Builder = PageNavigationEntry_CranePage_Builder;
class PageNavigationEntry_CranePage_WithParent {
  /**
   * @param {PageNavigationEntry_CranePage} value
   * @param {PageNavigationEntry_CranePage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_CranePage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasChestGroupKey() {
    if (this._cachedHasChestGroupKey === undefined) {
      if (this._value.hasChestGroupKey) {
        this._cachedHasChestGroupKey = true;
      } else {
        this._cachedHasChestGroupKey = this._parent ? this._parent.hasChestGroupKey : false;
      }
    }
    return this._cachedHasChestGroupKey;
  }
  get chestGroupKey() {
    if (this._cachedChestGroupKey === undefined) {
      if (this._value.hasChestGroupKey) {
        this._cachedChestGroupKey = this._value.chestGroupKey;
      } else {
        this._cachedChestGroupKey = this._parent ? this._parent.chestGroupKey : this._value.chestGroupKey;
      }
    }
    return this._cachedChestGroupKey;
  }
  get hasSubPage() {
    if (this._cachedHasSubPage === undefined) {
      if (this._value.hasSubPage) {
        this._cachedHasSubPage = true;
      } else {
        this._cachedHasSubPage = this._parent ? this._parent.hasSubPage : false;
      }
    }
    return this._cachedHasSubPage;
  }
  get subPage() {
    if (this._cachedSubPage === undefined) {
      if (this._value.hasSubPage) {
        this._cachedSubPage = this._value.subPage;
      } else {
        this._cachedSubPage = this._parent ? this._parent.subPage : this._value.subPage;
      }
    }
    return this._cachedSubPage;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_CranePage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_CranePage_WithParent.default = new PageNavigationEntry_CranePage_WithParent(PageNavigationEntry_CranePage.default, null);
PageNavigationEntry_CranePage.WithParent = PageNavigationEntry_CranePage_WithParent;
class PageNavigationEntry_CranePage_Descriptor {
  get chestGroupKey() {
    if (!this._chestGroupKey) {
      this._chestGroupKey = {
        name: "chestGroupKey",
        Name: "ChestGroupKey",
        number: 1,
        protoType: 9,
        protoName: "chest_group_key",
        type: JsonToString,
        options: {
          valueDropdown: "ChestGroupKeyList",
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._chestGroupKey;
  }
  get subPage() {
    if (!this._subPage) {
      this._subPage = {
        name: "subPage",
        Name: "SubPage",
        number: 2,
        protoType: 14,
        protoName: "sub_page",
        type: PageNavigationEntry_CranePage_SubPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._subPage;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.chestGroupKey,
        this.subPage,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForChestGroupKey",
        Name: "DummyOneofForChestGroupKey",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForSubPage",
        Name: "DummyOneofForSubPage",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_CranePage.descriptor = new PageNavigationEntry_CranePage_Descriptor();
class PageNavigationEntry_PortalPage {
  get descriptor() {
    return PageNavigationEntry_PortalPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasDungeonIndex = false;
    this._dungeonIndex = null;
  }
  isEmpty() {
    if (this._hasDungeonIndex) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_PortalPage) {
      return value;
    }
    return PageNavigationEntry_PortalPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_PortalPage();
    if (json.dungeonIndex != null) {
      result._hasDungeonIndex = true;
      result._dungeonIndex = JsonToInt32.fromJson(json.dungeonIndex);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasDungeonIndex) {
      result.dungeonIndex = JsonToInt32.toJson(this._dungeonIndex);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get dungeonIndex() {
    return this._hasDungeonIndex ? this._dungeonIndex : JsonToInt32.default;
  }
  get hasDungeonIndex() {
    return this._hasDungeonIndex;
  }
  setDungeonIndex(path, value) {
    return {type: 'SET', path: path.concat(['dungeonIndex']), value};
  }
  deleteDungeonIndex(path) {
    return {type: 'DELETE', path: path.concat(['dungeonIndex'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "dungeonIndex": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_PortalPage(this);
              copy._hasDungeonIndex = true;
              copy._dungeonIndex = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasDungeonIndex) {
                return this;
              }
              const copy = new PageNavigationEntry_PortalPage(this);
              copy._hasDungeonIndex = false;
              copy._dungeonIndex = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_PortalPage} other
   * @returns {PageNavigationEntry_PortalPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_PortalPage(this);
    if (other._hasDungeonIndex) {
      copy._hasDungeonIndex = true;
      copy._dungeonIndex = other._dungeonIndex;
    }
    return copy;
  }
}
PageNavigationEntry_PortalPage.default = new PageNavigationEntry_PortalPage();
class PageNavigationEntry_PortalPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_PortalPage(value);
  }
  build() {
    return new PageNavigationEntry_PortalPage(this._value);
  }
  setDungeonIndex(value) {
    this._value._hasDungeonIndex = true;
    this._value._dungeonIndex = value;
    return this;
  }
  deleteDungeonIndex() {
    this._value._hasDungeonIndex = false;
    this._value._dungeonIndex = null;
    return this;
  }
}
PageNavigationEntry_PortalPage.Builder = PageNavigationEntry_PortalPage_Builder;
class PageNavigationEntry_PortalPage_WithParent {
  /**
   * @param {PageNavigationEntry_PortalPage} value
   * @param {PageNavigationEntry_PortalPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_PortalPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasDungeonIndex() {
    if (this._cachedHasDungeonIndex === undefined) {
      if (this._value.hasDungeonIndex) {
        this._cachedHasDungeonIndex = true;
      } else {
        this._cachedHasDungeonIndex = this._parent ? this._parent.hasDungeonIndex : false;
      }
    }
    return this._cachedHasDungeonIndex;
  }
  get dungeonIndex() {
    if (this._cachedDungeonIndex === undefined) {
      if (this._value.hasDungeonIndex) {
        this._cachedDungeonIndex = this._value.dungeonIndex;
      } else {
        this._cachedDungeonIndex = this._parent ? this._parent.dungeonIndex : this._value.dungeonIndex;
      }
    }
    return this._cachedDungeonIndex;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_PortalPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_PortalPage_WithParent.default = new PageNavigationEntry_PortalPage_WithParent(PageNavigationEntry_PortalPage.default, null);
PageNavigationEntry_PortalPage.WithParent = PageNavigationEntry_PortalPage_WithParent;
class PageNavigationEntry_PortalPage_Descriptor {
  get dungeonIndex() {
    if (!this._dungeonIndex) {
      this._dungeonIndex = {
        name: "dungeonIndex",
        Name: "DungeonIndex",
        number: 1,
        protoType: 5,
        protoName: "dungeon_index",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._dungeonIndex;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.dungeonIndex,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForDungeonIndex",
        Name: "DummyOneofForDungeonIndex",
        fields: [
          this.fields[0],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_PortalPage.descriptor = new PageNavigationEntry_PortalPage_Descriptor();
class PageNavigationEntry_MinePage {
  get descriptor() {
    return PageNavigationEntry_MinePage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasFloor = false;
    this._floor = null;
  }
  isEmpty() {
    if (this._hasFloor) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_MinePage) {
      return value;
    }
    return PageNavigationEntry_MinePage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_MinePage();
    if (json.floor != null) {
      result._hasFloor = true;
      result._floor = JsonToInt32.fromJson(json.floor);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasFloor) {
      result.floor = JsonToInt32.toJson(this._floor);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get floor() {
    return this._hasFloor ? this._floor : JsonToInt32.default;
  }
  get hasFloor() {
    return this._hasFloor;
  }
  setFloor(path, value) {
    return {type: 'SET', path: path.concat(['floor']), value};
  }
  deleteFloor(path) {
    return {type: 'DELETE', path: path.concat(['floor'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "floor": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_MinePage(this);
              copy._hasFloor = true;
              copy._floor = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasFloor) {
                return this;
              }
              const copy = new PageNavigationEntry_MinePage(this);
              copy._hasFloor = false;
              copy._floor = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_MinePage} other
   * @returns {PageNavigationEntry_MinePage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_MinePage(this);
    if (other._hasFloor) {
      copy._hasFloor = true;
      copy._floor = other._floor;
    }
    return copy;
  }
}
PageNavigationEntry_MinePage.default = new PageNavigationEntry_MinePage();
class PageNavigationEntry_MinePage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_MinePage(value);
  }
  build() {
    return new PageNavigationEntry_MinePage(this._value);
  }
  setFloor(value) {
    this._value._hasFloor = true;
    this._value._floor = value;
    return this;
  }
  deleteFloor() {
    this._value._hasFloor = false;
    this._value._floor = null;
    return this;
  }
}
PageNavigationEntry_MinePage.Builder = PageNavigationEntry_MinePage_Builder;
class PageNavigationEntry_MinePage_WithParent {
  /**
   * @param {PageNavigationEntry_MinePage} value
   * @param {PageNavigationEntry_MinePage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_MinePage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasFloor() {
    if (this._cachedHasFloor === undefined) {
      if (this._value.hasFloor) {
        this._cachedHasFloor = true;
      } else {
        this._cachedHasFloor = this._parent ? this._parent.hasFloor : false;
      }
    }
    return this._cachedHasFloor;
  }
  get floor() {
    if (this._cachedFloor === undefined) {
      if (this._value.hasFloor) {
        this._cachedFloor = this._value.floor;
      } else {
        this._cachedFloor = this._parent ? this._parent.floor : this._value.floor;
      }
    }
    return this._cachedFloor;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_MinePage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_MinePage_WithParent.default = new PageNavigationEntry_MinePage_WithParent(PageNavigationEntry_MinePage.default, null);
PageNavigationEntry_MinePage.WithParent = PageNavigationEntry_MinePage_WithParent;
class PageNavigationEntry_MinePage_Descriptor {
  get floor() {
    if (!this._floor) {
      this._floor = {
        name: "floor",
        Name: "Floor",
        number: 2,
        protoType: 5,
        protoName: "floor",
        type: JsonToInt32,
        options: {
          min: 1,
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._floor;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.floor,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForFloor",
        Name: "DummyOneofForFloor",
        fields: [
          this.fields[0],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_MinePage.descriptor = new PageNavigationEntry_MinePage_Descriptor();
class PageNavigationEntry_MarketPage {
  get descriptor() {
    return PageNavigationEntry_MarketPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasTabKey = false;
    this._tabKey = null;
  }
  isEmpty() {
    if (this._hasTabKey) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_MarketPage) {
      return value;
    }
    return PageNavigationEntry_MarketPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_MarketPage();
    if (json.tabKey != null) {
      result._hasTabKey = true;
      result._tabKey = JsonToString.fromJson(json.tabKey);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasTabKey) {
      result.tabKey = JsonToString.toJson(this._tabKey);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get tabKey() {
    return this._hasTabKey ? this._tabKey : JsonToString.default;
  }
  get hasTabKey() {
    return this._hasTabKey;
  }
  setTabKey(path, value) {
    return {type: 'SET', path: path.concat(['tabKey']), value};
  }
  deleteTabKey(path) {
    return {type: 'DELETE', path: path.concat(['tabKey'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "tabKey": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_MarketPage(this);
              copy._hasTabKey = true;
              copy._tabKey = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasTabKey) {
                return this;
              }
              const copy = new PageNavigationEntry_MarketPage(this);
              copy._hasTabKey = false;
              copy._tabKey = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_MarketPage} other
   * @returns {PageNavigationEntry_MarketPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_MarketPage(this);
    if (other._hasTabKey) {
      copy._hasTabKey = true;
      copy._tabKey = other._tabKey;
    }
    return copy;
  }
}
PageNavigationEntry_MarketPage.default = new PageNavigationEntry_MarketPage();
class PageNavigationEntry_MarketPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_MarketPage(value);
  }
  build() {
    return new PageNavigationEntry_MarketPage(this._value);
  }
  setTabKey(value) {
    this._value._hasTabKey = true;
    this._value._tabKey = value;
    return this;
  }
  deleteTabKey() {
    this._value._hasTabKey = false;
    this._value._tabKey = null;
    return this;
  }
}
PageNavigationEntry_MarketPage.Builder = PageNavigationEntry_MarketPage_Builder;
class PageNavigationEntry_MarketPage_WithParent {
  /**
   * @param {PageNavigationEntry_MarketPage} value
   * @param {PageNavigationEntry_MarketPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_MarketPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasTabKey() {
    if (this._cachedHasTabKey === undefined) {
      if (this._value.hasTabKey) {
        this._cachedHasTabKey = true;
      } else {
        this._cachedHasTabKey = this._parent ? this._parent.hasTabKey : false;
      }
    }
    return this._cachedHasTabKey;
  }
  get tabKey() {
    if (this._cachedTabKey === undefined) {
      if (this._value.hasTabKey) {
        this._cachedTabKey = this._value.tabKey;
      } else {
        this._cachedTabKey = this._parent ? this._parent.tabKey : this._value.tabKey;
      }
    }
    return this._cachedTabKey;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_MarketPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_MarketPage_WithParent.default = new PageNavigationEntry_MarketPage_WithParent(PageNavigationEntry_MarketPage.default, null);
PageNavigationEntry_MarketPage.WithParent = PageNavigationEntry_MarketPage_WithParent;
class PageNavigationEntry_MarketPage_Descriptor {
  get tabKey() {
    if (!this._tabKey) {
      this._tabKey = {
        name: "tabKey",
        Name: "TabKey",
        number: 1,
        protoType: 9,
        protoName: "tab_key",
        type: JsonToString,
        options: {
          valueDropdown: "MarketTabKeyList",
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._tabKey;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.tabKey,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForTabKey",
        Name: "DummyOneofForTabKey",
        fields: [
          this.fields[0],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_MarketPage.descriptor = new PageNavigationEntry_MarketPage_Descriptor();
class PageNavigationEntry_ShopPage {
  get descriptor() {
    return PageNavigationEntry_ShopPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasProductId = false;
    this._productId = null;
  }
  isEmpty() {
    if (this._hasProductId) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_ShopPage) {
      return value;
    }
    return PageNavigationEntry_ShopPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_ShopPage();
    if (json.productId != null) {
      result._hasProductId = true;
      result._productId = JsonToString.fromJson(json.productId);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasProductId) {
      result.productId = JsonToString.toJson(this._productId);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get productId() {
    return this._hasProductId ? this._productId : JsonToString.default;
  }
  get hasProductId() {
    return this._hasProductId;
  }
  setProductId(path, value) {
    return {type: 'SET', path: path.concat(['productId']), value};
  }
  deleteProductId(path) {
    return {type: 'DELETE', path: path.concat(['productId'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "productId": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_ShopPage(this);
              copy._hasProductId = true;
              copy._productId = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasProductId) {
                return this;
              }
              const copy = new PageNavigationEntry_ShopPage(this);
              copy._hasProductId = false;
              copy._productId = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_ShopPage} other
   * @returns {PageNavigationEntry_ShopPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_ShopPage(this);
    if (other._hasProductId) {
      copy._hasProductId = true;
      copy._productId = other._productId;
    }
    return copy;
  }
}
PageNavigationEntry_ShopPage.default = new PageNavigationEntry_ShopPage();
class PageNavigationEntry_ShopPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_ShopPage(value);
  }
  build() {
    return new PageNavigationEntry_ShopPage(this._value);
  }
  setProductId(value) {
    this._value._hasProductId = true;
    this._value._productId = value;
    return this;
  }
  deleteProductId() {
    this._value._hasProductId = false;
    this._value._productId = null;
    return this;
  }
}
PageNavigationEntry_ShopPage.Builder = PageNavigationEntry_ShopPage_Builder;
class PageNavigationEntry_ShopPage_WithParent {
  /**
   * @param {PageNavigationEntry_ShopPage} value
   * @param {PageNavigationEntry_ShopPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_ShopPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasProductId() {
    if (this._cachedHasProductId === undefined) {
      if (this._value.hasProductId) {
        this._cachedHasProductId = true;
      } else {
        this._cachedHasProductId = this._parent ? this._parent.hasProductId : false;
      }
    }
    return this._cachedHasProductId;
  }
  get productId() {
    if (this._cachedProductId === undefined) {
      if (this._value.hasProductId) {
        this._cachedProductId = this._value.productId;
      } else {
        this._cachedProductId = this._parent ? this._parent.productId : this._value.productId;
      }
    }
    return this._cachedProductId;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_ShopPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_ShopPage_WithParent.default = new PageNavigationEntry_ShopPage_WithParent(PageNavigationEntry_ShopPage.default, null);
PageNavigationEntry_ShopPage.WithParent = PageNavigationEntry_ShopPage_WithParent;
class PageNavigationEntry_ShopPage_Descriptor {
  get productId() {
    if (!this._productId) {
      this._productId = {
        name: "productId",
        Name: "ProductId",
        number: 1,
        protoType: 9,
        protoName: "product_id",
        type: JsonToString,
        options: {
          valueDropdown: "ShopProductIdList",
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._productId;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.productId,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForProductId",
        Name: "DummyOneofForProductId",
        fields: [
          this.fields[0],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_ShopPage.descriptor = new PageNavigationEntry_ShopPage_Descriptor();
class PageNavigationEntry_CardListPage {
  get descriptor() {
    return PageNavigationEntry_CardListPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
  }
  isEmpty() {
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_CardListPage) {
      return value;
    }
    return PageNavigationEntry_CardListPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_CardListPage();
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    return result;
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_CardListPage} other
   * @returns {PageNavigationEntry_CardListPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_CardListPage(this);
    return copy;
  }
}
PageNavigationEntry_CardListPage.default = new PageNavigationEntry_CardListPage();
class PageNavigationEntry_CardListPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_CardListPage(value);
  }
  build() {
    return new PageNavigationEntry_CardListPage(this._value);
  }
}
PageNavigationEntry_CardListPage.Builder = PageNavigationEntry_CardListPage_Builder;
class PageNavigationEntry_CardListPage_WithParent {
  /**
   * @param {PageNavigationEntry_CardListPage} value
   * @param {PageNavigationEntry_CardListPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_CardListPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_CardListPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_CardListPage_WithParent.default = new PageNavigationEntry_CardListPage_WithParent(PageNavigationEntry_CardListPage.default, null);
PageNavigationEntry_CardListPage.WithParent = PageNavigationEntry_CardListPage_WithParent;
class PageNavigationEntry_CardListPage_Descriptor {
  get fields() {
    if (!this._fields) {
      this._fields = [
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
    }
    return this._oneofs;
  }
}
PageNavigationEntry_CardListPage.descriptor = new PageNavigationEntry_CardListPage_Descriptor();
class PageNavigationEntry_CampaignPage {
  get descriptor() {
    return PageNavigationEntry_CampaignPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasWorldIndex = false;
    this._worldIndex = null;
  }
  isEmpty() {
    if (this._hasWorldIndex) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_CampaignPage) {
      return value;
    }
    return PageNavigationEntry_CampaignPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_CampaignPage();
    if (json.worldIndex != null) {
      result._hasWorldIndex = true;
      result._worldIndex = JsonToInt32.fromJson(json.worldIndex);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasWorldIndex) {
      result.worldIndex = JsonToInt32.toJson(this._worldIndex);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get worldIndex() {
    return this._hasWorldIndex ? this._worldIndex : JsonToInt32.default;
  }
  get hasWorldIndex() {
    return this._hasWorldIndex;
  }
  setWorldIndex(path, value) {
    return {type: 'SET', path: path.concat(['worldIndex']), value};
  }
  deleteWorldIndex(path) {
    return {type: 'DELETE', path: path.concat(['worldIndex'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "worldIndex": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_CampaignPage(this);
              copy._hasWorldIndex = true;
              copy._worldIndex = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasWorldIndex) {
                return this;
              }
              const copy = new PageNavigationEntry_CampaignPage(this);
              copy._hasWorldIndex = false;
              copy._worldIndex = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_CampaignPage} other
   * @returns {PageNavigationEntry_CampaignPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_CampaignPage(this);
    if (other._hasWorldIndex) {
      copy._hasWorldIndex = true;
      copy._worldIndex = other._worldIndex;
    }
    return copy;
  }
}
PageNavigationEntry_CampaignPage.default = new PageNavigationEntry_CampaignPage();
class PageNavigationEntry_CampaignPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_CampaignPage(value);
  }
  build() {
    return new PageNavigationEntry_CampaignPage(this._value);
  }
  setWorldIndex(value) {
    this._value._hasWorldIndex = true;
    this._value._worldIndex = value;
    return this;
  }
  deleteWorldIndex() {
    this._value._hasWorldIndex = false;
    this._value._worldIndex = null;
    return this;
  }
}
PageNavigationEntry_CampaignPage.Builder = PageNavigationEntry_CampaignPage_Builder;
class PageNavigationEntry_CampaignPage_WithParent {
  /**
   * @param {PageNavigationEntry_CampaignPage} value
   * @param {PageNavigationEntry_CampaignPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_CampaignPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasWorldIndex() {
    if (this._cachedHasWorldIndex === undefined) {
      if (this._value.hasWorldIndex) {
        this._cachedHasWorldIndex = true;
      } else {
        this._cachedHasWorldIndex = this._parent ? this._parent.hasWorldIndex : false;
      }
    }
    return this._cachedHasWorldIndex;
  }
  get worldIndex() {
    if (this._cachedWorldIndex === undefined) {
      if (this._value.hasWorldIndex) {
        this._cachedWorldIndex = this._value.worldIndex;
      } else {
        this._cachedWorldIndex = this._parent ? this._parent.worldIndex : this._value.worldIndex;
      }
    }
    return this._cachedWorldIndex;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_CampaignPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_CampaignPage_WithParent.default = new PageNavigationEntry_CampaignPage_WithParent(PageNavigationEntry_CampaignPage.default, null);
PageNavigationEntry_CampaignPage.WithParent = PageNavigationEntry_CampaignPage_WithParent;
class PageNavigationEntry_CampaignPage_Descriptor {
  get worldIndex() {
    if (!this._worldIndex) {
      this._worldIndex = {
        name: "worldIndex",
        Name: "WorldIndex",
        number: 1,
        protoType: 5,
        protoName: "world_index",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._worldIndex;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.worldIndex,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForWorldIndex",
        Name: "DummyOneofForWorldIndex",
        fields: [
          this.fields[0],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_CampaignPage.descriptor = new PageNavigationEntry_CampaignPage_Descriptor();
class PageNavigationEntry_LoadoutPage_CampaignEntry {
  get descriptor() {
    return PageNavigationEntry_LoadoutPage_CampaignEntry.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasWorldIndex = false;
    this._worldIndex = null;
    this._hasLevelIndex = false;
    this._levelIndex = null;
    this._hasChallengeMode = false;
    this._challengeMode = null;
  }
  isEmpty() {
    if (this._hasWorldIndex) return false;
    if (this._hasLevelIndex) return false;
    if (this._hasChallengeMode) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_LoadoutPage_CampaignEntry) {
      return value;
    }
    return PageNavigationEntry_LoadoutPage_CampaignEntry.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_LoadoutPage_CampaignEntry();
    if (json.worldIndex != null) {
      result._hasWorldIndex = true;
      result._worldIndex = JsonToInt32.fromJson(json.worldIndex);
    }
    if (json.levelIndex != null) {
      result._hasLevelIndex = true;
      result._levelIndex = JsonToInt32.fromJson(json.levelIndex);
    }
    if (json.challengeMode != null) {
      result._hasChallengeMode = true;
      result._challengeMode = JsonToBoolean.fromJson(json.challengeMode);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasWorldIndex) {
      result.worldIndex = JsonToInt32.toJson(this._worldIndex);
    }
    if (this._hasLevelIndex) {
      result.levelIndex = JsonToInt32.toJson(this._levelIndex);
    }
    if (this._hasChallengeMode) {
      result.challengeMode = JsonToBoolean.toJson(this._challengeMode);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get worldIndex() {
    return this._hasWorldIndex ? this._worldIndex : JsonToInt32.default;
  }
  get hasWorldIndex() {
    return this._hasWorldIndex;
  }
  setWorldIndex(path, value) {
    return {type: 'SET', path: path.concat(['worldIndex']), value};
  }
  deleteWorldIndex(path) {
    return {type: 'DELETE', path: path.concat(['worldIndex'])};
  }
  /**
   * @returns {number}
   */
  get levelIndex() {
    return this._hasLevelIndex ? this._levelIndex : JsonToInt32.default;
  }
  get hasLevelIndex() {
    return this._hasLevelIndex;
  }
  setLevelIndex(path, value) {
    return {type: 'SET', path: path.concat(['levelIndex']), value};
  }
  deleteLevelIndex(path) {
    return {type: 'DELETE', path: path.concat(['levelIndex'])};
  }
  /**
   * @returns {boolean}
   */
  get challengeMode() {
    return this._hasChallengeMode ? this._challengeMode : JsonToBoolean.default;
  }
  get hasChallengeMode() {
    return this._hasChallengeMode;
  }
  setChallengeMode(path, value) {
    return {type: 'SET', path: path.concat(['challengeMode']), value};
  }
  deleteChallengeMode(path) {
    return {type: 'DELETE', path: path.concat(['challengeMode'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "worldIndex": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_LoadoutPage_CampaignEntry(this);
              copy._hasWorldIndex = true;
              copy._worldIndex = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasWorldIndex) {
                return this;
              }
              const copy = new PageNavigationEntry_LoadoutPage_CampaignEntry(this);
              copy._hasWorldIndex = false;
              copy._worldIndex = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "levelIndex": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_LoadoutPage_CampaignEntry(this);
              copy._hasLevelIndex = true;
              copy._levelIndex = JsonToInt32.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasLevelIndex) {
                return this;
              }
              const copy = new PageNavigationEntry_LoadoutPage_CampaignEntry(this);
              copy._hasLevelIndex = false;
              copy._levelIndex = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "challengeMode": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_LoadoutPage_CampaignEntry(this);
              copy._hasChallengeMode = true;
              copy._challengeMode = JsonToBoolean.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasChallengeMode) {
                return this;
              }
              const copy = new PageNavigationEntry_LoadoutPage_CampaignEntry(this);
              copy._hasChallengeMode = false;
              copy._challengeMode = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_LoadoutPage_CampaignEntry} other
   * @returns {PageNavigationEntry_LoadoutPage_CampaignEntry}
   */
  merge(other) {
    const copy = new PageNavigationEntry_LoadoutPage_CampaignEntry(this);
    if (other._hasWorldIndex) {
      copy._hasWorldIndex = true;
      copy._worldIndex = other._worldIndex;
    }
    if (other._hasLevelIndex) {
      copy._hasLevelIndex = true;
      copy._levelIndex = other._levelIndex;
    }
    if (other._hasChallengeMode) {
      copy._hasChallengeMode = true;
      copy._challengeMode = other._challengeMode;
    }
    return copy;
  }
}
PageNavigationEntry_LoadoutPage_CampaignEntry.default = new PageNavigationEntry_LoadoutPage_CampaignEntry();
class PageNavigationEntry_LoadoutPage_CampaignEntry_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_LoadoutPage_CampaignEntry(value);
  }
  build() {
    return new PageNavigationEntry_LoadoutPage_CampaignEntry(this._value);
  }
  setWorldIndex(value) {
    this._value._hasWorldIndex = true;
    this._value._worldIndex = value;
    return this;
  }
  deleteWorldIndex() {
    this._value._hasWorldIndex = false;
    this._value._worldIndex = null;
    return this;
  }
  setLevelIndex(value) {
    this._value._hasLevelIndex = true;
    this._value._levelIndex = value;
    return this;
  }
  deleteLevelIndex() {
    this._value._hasLevelIndex = false;
    this._value._levelIndex = null;
    return this;
  }
  setChallengeMode(value) {
    this._value._hasChallengeMode = true;
    this._value._challengeMode = value;
    return this;
  }
  deleteChallengeMode() {
    this._value._hasChallengeMode = false;
    this._value._challengeMode = null;
    return this;
  }
}
PageNavigationEntry_LoadoutPage_CampaignEntry.Builder = PageNavigationEntry_LoadoutPage_CampaignEntry_Builder;
class PageNavigationEntry_LoadoutPage_CampaignEntry_WithParent {
  /**
   * @param {PageNavigationEntry_LoadoutPage_CampaignEntry} value
   * @param {PageNavigationEntry_LoadoutPage_CampaignEntry_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_LoadoutPage_CampaignEntry.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasWorldIndex() {
    if (this._cachedHasWorldIndex === undefined) {
      if (this._value.hasWorldIndex) {
        this._cachedHasWorldIndex = true;
      } else {
        this._cachedHasWorldIndex = this._parent ? this._parent.hasWorldIndex : false;
      }
    }
    return this._cachedHasWorldIndex;
  }
  get worldIndex() {
    if (this._cachedWorldIndex === undefined) {
      if (this._value.hasWorldIndex) {
        this._cachedWorldIndex = this._value.worldIndex;
      } else {
        this._cachedWorldIndex = this._parent ? this._parent.worldIndex : this._value.worldIndex;
      }
    }
    return this._cachedWorldIndex;
  }
  get hasLevelIndex() {
    if (this._cachedHasLevelIndex === undefined) {
      if (this._value.hasLevelIndex) {
        this._cachedHasLevelIndex = true;
      } else {
        this._cachedHasLevelIndex = this._parent ? this._parent.hasLevelIndex : false;
      }
    }
    return this._cachedHasLevelIndex;
  }
  get levelIndex() {
    if (this._cachedLevelIndex === undefined) {
      if (this._value.hasLevelIndex) {
        this._cachedLevelIndex = this._value.levelIndex;
      } else {
        this._cachedLevelIndex = this._parent ? this._parent.levelIndex : this._value.levelIndex;
      }
    }
    return this._cachedLevelIndex;
  }
  get hasChallengeMode() {
    if (this._cachedHasChallengeMode === undefined) {
      if (this._value.hasChallengeMode) {
        this._cachedHasChallengeMode = true;
      } else {
        this._cachedHasChallengeMode = this._parent ? this._parent.hasChallengeMode : false;
      }
    }
    return this._cachedHasChallengeMode;
  }
  get challengeMode() {
    if (this._cachedChallengeMode === undefined) {
      if (this._value.hasChallengeMode) {
        this._cachedChallengeMode = this._value.challengeMode;
      } else {
        this._cachedChallengeMode = this._parent ? this._parent.challengeMode : this._value.challengeMode;
      }
    }
    return this._cachedChallengeMode;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_LoadoutPage_CampaignEntry_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_LoadoutPage_CampaignEntry_WithParent.default = new PageNavigationEntry_LoadoutPage_CampaignEntry_WithParent(PageNavigationEntry_LoadoutPage_CampaignEntry.default, null);
PageNavigationEntry_LoadoutPage_CampaignEntry.WithParent = PageNavigationEntry_LoadoutPage_CampaignEntry_WithParent;
class PageNavigationEntry_LoadoutPage_CampaignEntry_Descriptor {
  get worldIndex() {
    if (!this._worldIndex) {
      this._worldIndex = {
        name: "worldIndex",
        Name: "WorldIndex",
        number: 1,
        protoType: 5,
        protoName: "world_index",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._worldIndex;
  }
  get levelIndex() {
    if (!this._levelIndex) {
      this._levelIndex = {
        name: "levelIndex",
        Name: "LevelIndex",
        number: 2,
        protoType: 5,
        protoName: "level_index",
        type: JsonToInt32,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._levelIndex;
  }
  get challengeMode() {
    if (!this._challengeMode) {
      this._challengeMode = {
        name: "challengeMode",
        Name: "ChallengeMode",
        number: 3,
        protoType: 8,
        protoName: "challenge_mode",
        type: JsonToBoolean,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._challengeMode;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.worldIndex,
        this.levelIndex,
        this.challengeMode,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForWorldIndex",
        Name: "DummyOneofForWorldIndex",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForLevelIndex",
        Name: "DummyOneofForLevelIndex",
        fields: [
          this.fields[1],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForChallengeMode",
        Name: "DummyOneofForChallengeMode",
        fields: [
          this.fields[2],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_LoadoutPage_CampaignEntry.descriptor = new PageNavigationEntry_LoadoutPage_CampaignEntry_Descriptor();
class PageNavigationEntry_LoadoutPage {
  get descriptor() {
    return PageNavigationEntry_LoadoutPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasCampaign = false;
    this._campaign = null;
  }
  isEmpty() {
    if (this._hasCampaign && !this._campaign.isEmpty()) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_LoadoutPage) {
      return value;
    }
    return PageNavigationEntry_LoadoutPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_LoadoutPage();
    if (json.campaign != null) {
      result._hasCampaign = true;
      result._campaign = PageNavigationEntry_LoadoutPage_CampaignEntry.fromJson(json.campaign);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasCampaign && !this._campaign.isEmpty()) {
      result.campaign = PageNavigationEntry_LoadoutPage_CampaignEntry.toJson(this._campaign);
    }
    return result;
  }
  /**
   * @returns {PageNavigationEntry_LoadoutPage_CampaignEntry}
   */
  get campaign() {
    return this._hasCampaign ? this._campaign : PageNavigationEntry_LoadoutPage_CampaignEntry.default;
  }
  get hasCampaign() {
    return this._hasCampaign;
  }
  setCampaign(path, value) {
    return {type: 'SET', path: path.concat(['campaign']), value: value.toJson()};
  }
  deleteCampaign(path) {
    return {type: 'DELETE', path: path.concat(['campaign'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "campaign": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_LoadoutPage(this);
              copy._hasCampaign = true;
              copy._campaign = PageNavigationEntry_LoadoutPage_CampaignEntry.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasCampaign) {
                return this;
              }
              const copy = new PageNavigationEntry_LoadoutPage(this);
              copy._hasCampaign = false;
              copy._campaign = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (!this._hasCampaign) {
            const copy = new PageNavigationEntry_LoadoutPage(this);
            copy._hasCampaign = true;
            copy._campaign = PageNavigationEntry_LoadoutPage_CampaignEntry.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this._campaign.apply(type, path.slice(1), value);
          if (newValue === this._campaign) {
            return this;
          }
          const copy = new PageNavigationEntry_LoadoutPage(this);
          copy._campaign = copy._campaign.apply(type, path.slice(1), value);
          return copy;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_LoadoutPage} other
   * @returns {PageNavigationEntry_LoadoutPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_LoadoutPage(this);
    if (other._hasCampaign) {
      if (copy._hasCampaign) {
        copy._campaign = copy.campaign.merge(other.campaign);
      } else {
        copy._campaign = other._campaign;
      }
      copy._hasCampaign = true;
    }
    return copy;
  }
}
PageNavigationEntry_LoadoutPage.default = new PageNavigationEntry_LoadoutPage();
PageNavigationEntry_LoadoutPage.CampaignEntry = PageNavigationEntry_LoadoutPage_CampaignEntry;
class PageNavigationEntry_LoadoutPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_LoadoutPage(value);
  }
  build() {
    return new PageNavigationEntry_LoadoutPage(this._value);
  }
  setCampaign(value) {
    this._value._hasCampaign = true;
    this._value._campaign = value;
    return this;
  }
  deleteCampaign() {
    this._value._hasCampaign = false;
    this._value._campaign = null;
    return this;
  }
}
PageNavigationEntry_LoadoutPage.Builder = PageNavigationEntry_LoadoutPage_Builder;
class PageNavigationEntry_LoadoutPage_WithParent {
  /**
   * @param {PageNavigationEntry_LoadoutPage} value
   * @param {PageNavigationEntry_LoadoutPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_LoadoutPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasCampaign() {
    if (this._cachedHasCampaign === undefined) {
      if (this._value.hasCampaign) {
        this._cachedHasCampaign = true;
      } else {
        this._cachedHasCampaign = this._parent ? this._parent.hasCampaign : false;
      }
    }
    return this._cachedHasCampaign;
  }
  get campaign() {
    if (!this._cachedCampaign) {
      if (this._value.hasCampaign) {
        this._cachedCampaign = new PageNavigationEntry_LoadoutPage_CampaignEntry.WithParent(this._value.campaign, this._parent && this._parent.hasCampaign ? this._parent.campaign : null);
      } else {
        this._cachedCampaign = this._parent ? new PageNavigationEntry_LoadoutPage_CampaignEntry.WithParent(PageNavigationEntry_LoadoutPage_CampaignEntry.default, this._parent.campaign) : PageNavigationEntry_LoadoutPage_CampaignEntry.WithParent.default;
      }
    }
    return this._cachedCampaign;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_LoadoutPage_WithParent(current, parent);
    if (this._cachedCampaign && this._value.hasCampaign && current.hasCampaign) {
      if (this._value.campaign === current.campaign && this._parent === parent) {
        result._cachedCampaign = this._cachedCampaign;
      } else {
        result._cachedCampaign = this._cachedCampaign.update(current.campaign, parent && parent.hasCampaign ? parent.campaign : null);
      }
    }
    return result;
  }
}
PageNavigationEntry_LoadoutPage_WithParent.default = new PageNavigationEntry_LoadoutPage_WithParent(PageNavigationEntry_LoadoutPage.default, null);
PageNavigationEntry_LoadoutPage.WithParent = PageNavigationEntry_LoadoutPage_WithParent;
class PageNavigationEntry_LoadoutPage_Descriptor {
  get campaign() {
    if (!this._campaign) {
      this._campaign = {
        name: "campaign",
        Name: "Campaign",
        number: 1,
        protoType: 11,
        protoName: "campaign",
        type: PageNavigationEntry_LoadoutPage_CampaignEntry,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._campaign;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.campaign,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "entry",
        Name: "Entry",
        fields: [
          this.fields[0],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_LoadoutPage.descriptor = new PageNavigationEntry_LoadoutPage_Descriptor();
class PageNavigationEntry_FacebookPage {
  get descriptor() {
    return PageNavigationEntry_FacebookPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
  }
  isEmpty() {
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_FacebookPage) {
      return value;
    }
    return PageNavigationEntry_FacebookPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_FacebookPage();
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    return result;
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_FacebookPage} other
   * @returns {PageNavigationEntry_FacebookPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_FacebookPage(this);
    return copy;
  }
}
PageNavigationEntry_FacebookPage.default = new PageNavigationEntry_FacebookPage();
class PageNavigationEntry_FacebookPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_FacebookPage(value);
  }
  build() {
    return new PageNavigationEntry_FacebookPage(this._value);
  }
}
PageNavigationEntry_FacebookPage.Builder = PageNavigationEntry_FacebookPage_Builder;
class PageNavigationEntry_FacebookPage_WithParent {
  /**
   * @param {PageNavigationEntry_FacebookPage} value
   * @param {PageNavigationEntry_FacebookPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_FacebookPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_FacebookPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_FacebookPage_WithParent.default = new PageNavigationEntry_FacebookPage_WithParent(PageNavigationEntry_FacebookPage.default, null);
PageNavigationEntry_FacebookPage.WithParent = PageNavigationEntry_FacebookPage_WithParent;
class PageNavigationEntry_FacebookPage_Descriptor {
  get fields() {
    if (!this._fields) {
      this._fields = [
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
    }
    return this._oneofs;
  }
}
PageNavigationEntry_FacebookPage.descriptor = new PageNavigationEntry_FacebookPage_Descriptor();
class PageNavigationEntry_QuestPage_Tab {
  static fromJson(json) {
    if (typeof json === "string") {
      switch (json) {
        case "AUTO": return 0;
        case "QUEST": return 1;
        case "ACHIEVEMENT": return 2;
        default: return 0;
      }
    }
    return json;
  }
  static toJson(value) {
    return value;
  }
  static fromAny(value) {
    return PageNavigationEntry_QuestPage_Tab.fromJson(value);
  }
  static fromReact(e) {
    return PageNavigationEntry_QuestPage_Tab.fromJson(e);
  }
}
PageNavigationEntry_QuestPage_Tab.AUTO = 0;
PageNavigationEntry_QuestPage_Tab.QUEST = 1;
PageNavigationEntry_QuestPage_Tab.ACHIEVEMENT = 2;
PageNavigationEntry_QuestPage_Tab.default = 0;
PageNavigationEntry_QuestPage_Tab.values = {
  AUTO: 0,
  QUEST: 1,
  ACHIEVEMENT: 2,
};
PageNavigationEntry_QuestPage_Tab.names = {
  0: "AUTO",
  1: "QUEST",
  2: "ACHIEVEMENT",
};
class PageNavigationEntry_QuestPage {
  get descriptor() {
    return PageNavigationEntry_QuestPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasTab = false;
    this._tab = null;
  }
  isEmpty() {
    if (this._hasTab) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_QuestPage) {
      return value;
    }
    return PageNavigationEntry_QuestPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_QuestPage();
    if (json.tab != null) {
      result._hasTab = true;
      result._tab = PageNavigationEntry_QuestPage_Tab.fromJson(json.tab);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasTab) {
      result.tab = PageNavigationEntry_QuestPage_Tab.toJson(this._tab);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get tab() {
    return this._hasTab ? this._tab : PageNavigationEntry_QuestPage_Tab.default;
  }
  get hasTab() {
    return this._hasTab;
  }
  setTab(path, value) {
    return {type: 'SET', path: path.concat(['tab']), value};
  }
  deleteTab(path) {
    return {type: 'DELETE', path: path.concat(['tab'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "tab": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_QuestPage(this);
              copy._hasTab = true;
              copy._tab = PageNavigationEntry_QuestPage_Tab.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasTab) {
                return this;
              }
              const copy = new PageNavigationEntry_QuestPage(this);
              copy._hasTab = false;
              copy._tab = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_QuestPage} other
   * @returns {PageNavigationEntry_QuestPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_QuestPage(this);
    if (other._hasTab) {
      copy._hasTab = true;
      copy._tab = other._tab;
    }
    return copy;
  }
}
PageNavigationEntry_QuestPage.default = new PageNavigationEntry_QuestPage();
PageNavigationEntry_QuestPage.Tab = PageNavigationEntry_QuestPage_Tab;
class PageNavigationEntry_QuestPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_QuestPage(value);
  }
  build() {
    return new PageNavigationEntry_QuestPage(this._value);
  }
  setTab(value) {
    this._value._hasTab = true;
    this._value._tab = value;
    return this;
  }
  deleteTab() {
    this._value._hasTab = false;
    this._value._tab = null;
    return this;
  }
}
PageNavigationEntry_QuestPage.Builder = PageNavigationEntry_QuestPage_Builder;
class PageNavigationEntry_QuestPage_WithParent {
  /**
   * @param {PageNavigationEntry_QuestPage} value
   * @param {PageNavigationEntry_QuestPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_QuestPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasTab() {
    if (this._cachedHasTab === undefined) {
      if (this._value.hasTab) {
        this._cachedHasTab = true;
      } else {
        this._cachedHasTab = this._parent ? this._parent.hasTab : false;
      }
    }
    return this._cachedHasTab;
  }
  get tab() {
    if (this._cachedTab === undefined) {
      if (this._value.hasTab) {
        this._cachedTab = this._value.tab;
      } else {
        this._cachedTab = this._parent ? this._parent.tab : this._value.tab;
      }
    }
    return this._cachedTab;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_QuestPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_QuestPage_WithParent.default = new PageNavigationEntry_QuestPage_WithParent(PageNavigationEntry_QuestPage.default, null);
PageNavigationEntry_QuestPage.WithParent = PageNavigationEntry_QuestPage_WithParent;
class PageNavigationEntry_QuestPage_Descriptor {
  get tab() {
    if (!this._tab) {
      this._tab = {
        name: "tab",
        Name: "Tab",
        number: 1,
        protoType: 14,
        protoName: "tab",
        type: PageNavigationEntry_QuestPage_Tab,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._tab;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.tab,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForTab",
        Name: "DummyOneofForTab",
        fields: [
          this.fields[0],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_QuestPage.descriptor = new PageNavigationEntry_QuestPage_Descriptor();
class PageNavigationEntry_Website {
  get descriptor() {
    return PageNavigationEntry_Website.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasUrl = false;
    this._url = null;
  }
  isEmpty() {
    if (this._hasUrl) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_Website) {
      return value;
    }
    return PageNavigationEntry_Website.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_Website();
    if (json.url != null) {
      result._hasUrl = true;
      result._url = JsonToString.fromJson(json.url);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasUrl) {
      result.url = JsonToString.toJson(this._url);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get url() {
    return this._hasUrl ? this._url : JsonToString.default;
  }
  get hasUrl() {
    return this._hasUrl;
  }
  setUrl(path, value) {
    return {type: 'SET', path: path.concat(['url']), value};
  }
  deleteUrl(path) {
    return {type: 'DELETE', path: path.concat(['url'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "url": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_Website(this);
              copy._hasUrl = true;
              copy._url = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasUrl) {
                return this;
              }
              const copy = new PageNavigationEntry_Website(this);
              copy._hasUrl = false;
              copy._url = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_Website} other
   * @returns {PageNavigationEntry_Website}
   */
  merge(other) {
    const copy = new PageNavigationEntry_Website(this);
    if (other._hasUrl) {
      copy._hasUrl = true;
      copy._url = other._url;
    }
    return copy;
  }
}
PageNavigationEntry_Website.default = new PageNavigationEntry_Website();
class PageNavigationEntry_Website_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_Website(value);
  }
  build() {
    return new PageNavigationEntry_Website(this._value);
  }
  setUrl(value) {
    this._value._hasUrl = true;
    this._value._url = value;
    return this;
  }
  deleteUrl() {
    this._value._hasUrl = false;
    this._value._url = null;
    return this;
  }
}
PageNavigationEntry_Website.Builder = PageNavigationEntry_Website_Builder;
class PageNavigationEntry_Website_WithParent {
  /**
   * @param {PageNavigationEntry_Website} value
   * @param {PageNavigationEntry_Website_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_Website.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasUrl() {
    if (this._cachedHasUrl === undefined) {
      if (this._value.hasUrl) {
        this._cachedHasUrl = true;
      } else {
        this._cachedHasUrl = this._parent ? this._parent.hasUrl : false;
      }
    }
    return this._cachedHasUrl;
  }
  get url() {
    if (this._cachedUrl === undefined) {
      if (this._value.hasUrl) {
        this._cachedUrl = this._value.url;
      } else {
        this._cachedUrl = this._parent ? this._parent.url : this._value.url;
      }
    }
    return this._cachedUrl;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_Website_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_Website_WithParent.default = new PageNavigationEntry_Website_WithParent(PageNavigationEntry_Website.default, null);
PageNavigationEntry_Website.WithParent = PageNavigationEntry_Website_WithParent;
class PageNavigationEntry_Website_Descriptor {
  get url() {
    if (!this._url) {
      this._url = {
        name: "url",
        Name: "Url",
        number: 1,
        protoType: 9,
        protoName: "url",
        type: JsonToString,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._url;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.url,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForUrl",
        Name: "DummyOneofForUrl",
        fields: [
          this.fields[0],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_Website.descriptor = new PageNavigationEntry_Website_Descriptor();
class PageNavigationEntry_ProfilePage {
  get descriptor() {
    return PageNavigationEntry_ProfilePage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
  }
  isEmpty() {
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_ProfilePage) {
      return value;
    }
    return PageNavigationEntry_ProfilePage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_ProfilePage();
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    return result;
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_ProfilePage} other
   * @returns {PageNavigationEntry_ProfilePage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_ProfilePage(this);
    return copy;
  }
}
PageNavigationEntry_ProfilePage.default = new PageNavigationEntry_ProfilePage();
class PageNavigationEntry_ProfilePage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_ProfilePage(value);
  }
  build() {
    return new PageNavigationEntry_ProfilePage(this._value);
  }
}
PageNavigationEntry_ProfilePage.Builder = PageNavigationEntry_ProfilePage_Builder;
class PageNavigationEntry_ProfilePage_WithParent {
  /**
   * @param {PageNavigationEntry_ProfilePage} value
   * @param {PageNavigationEntry_ProfilePage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_ProfilePage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_ProfilePage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_ProfilePage_WithParent.default = new PageNavigationEntry_ProfilePage_WithParent(PageNavigationEntry_ProfilePage.default, null);
PageNavigationEntry_ProfilePage.WithParent = PageNavigationEntry_ProfilePage_WithParent;
class PageNavigationEntry_ProfilePage_Descriptor {
  get fields() {
    if (!this._fields) {
      this._fields = [
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
    }
    return this._oneofs;
  }
}
PageNavigationEntry_ProfilePage.descriptor = new PageNavigationEntry_ProfilePage_Descriptor();
class PageNavigationEntry_SettingPage {
  get descriptor() {
    return PageNavigationEntry_SettingPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
  }
  isEmpty() {
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_SettingPage) {
      return value;
    }
    return PageNavigationEntry_SettingPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_SettingPage();
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    return result;
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_SettingPage} other
   * @returns {PageNavigationEntry_SettingPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_SettingPage(this);
    return copy;
  }
}
PageNavigationEntry_SettingPage.default = new PageNavigationEntry_SettingPage();
class PageNavigationEntry_SettingPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_SettingPage(value);
  }
  build() {
    return new PageNavigationEntry_SettingPage(this._value);
  }
}
PageNavigationEntry_SettingPage.Builder = PageNavigationEntry_SettingPage_Builder;
class PageNavigationEntry_SettingPage_WithParent {
  /**
   * @param {PageNavigationEntry_SettingPage} value
   * @param {PageNavigationEntry_SettingPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_SettingPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_SettingPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_SettingPage_WithParent.default = new PageNavigationEntry_SettingPage_WithParent(PageNavigationEntry_SettingPage.default, null);
PageNavigationEntry_SettingPage.WithParent = PageNavigationEntry_SettingPage_WithParent;
class PageNavigationEntry_SettingPage_Descriptor {
  get fields() {
    if (!this._fields) {
      this._fields = [
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
    }
    return this._oneofs;
  }
}
PageNavigationEntry_SettingPage.descriptor = new PageNavigationEntry_SettingPage_Descriptor();
class PageNavigationEntry_TournamentPage {
  get descriptor() {
    return PageNavigationEntry_TournamentPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
  }
  isEmpty() {
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_TournamentPage) {
      return value;
    }
    return PageNavigationEntry_TournamentPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_TournamentPage();
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    return result;
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_TournamentPage} other
   * @returns {PageNavigationEntry_TournamentPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_TournamentPage(this);
    return copy;
  }
}
PageNavigationEntry_TournamentPage.default = new PageNavigationEntry_TournamentPage();
class PageNavigationEntry_TournamentPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_TournamentPage(value);
  }
  build() {
    return new PageNavigationEntry_TournamentPage(this._value);
  }
}
PageNavigationEntry_TournamentPage.Builder = PageNavigationEntry_TournamentPage_Builder;
class PageNavigationEntry_TournamentPage_WithParent {
  /**
   * @param {PageNavigationEntry_TournamentPage} value
   * @param {PageNavigationEntry_TournamentPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_TournamentPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_TournamentPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_TournamentPage_WithParent.default = new PageNavigationEntry_TournamentPage_WithParent(PageNavigationEntry_TournamentPage.default, null);
PageNavigationEntry_TournamentPage.WithParent = PageNavigationEntry_TournamentPage_WithParent;
class PageNavigationEntry_TournamentPage_Descriptor {
  get fields() {
    if (!this._fields) {
      this._fields = [
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
    }
    return this._oneofs;
  }
}
PageNavigationEntry_TournamentPage.descriptor = new PageNavigationEntry_TournamentPage_Descriptor();
class PageNavigationEntry_FriendPage {
  get descriptor() {
    return PageNavigationEntry_FriendPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
  }
  isEmpty() {
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_FriendPage) {
      return value;
    }
    return PageNavigationEntry_FriendPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_FriendPage();
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    return result;
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_FriendPage} other
   * @returns {PageNavigationEntry_FriendPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_FriendPage(this);
    return copy;
  }
}
PageNavigationEntry_FriendPage.default = new PageNavigationEntry_FriendPage();
class PageNavigationEntry_FriendPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_FriendPage(value);
  }
  build() {
    return new PageNavigationEntry_FriendPage(this._value);
  }
}
PageNavigationEntry_FriendPage.Builder = PageNavigationEntry_FriendPage_Builder;
class PageNavigationEntry_FriendPage_WithParent {
  /**
   * @param {PageNavigationEntry_FriendPage} value
   * @param {PageNavigationEntry_FriendPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_FriendPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_FriendPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_FriendPage_WithParent.default = new PageNavigationEntry_FriendPage_WithParent(PageNavigationEntry_FriendPage.default, null);
PageNavigationEntry_FriendPage.WithParent = PageNavigationEntry_FriendPage_WithParent;
class PageNavigationEntry_FriendPage_Descriptor {
  get fields() {
    if (!this._fields) {
      this._fields = [
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
    }
    return this._oneofs;
  }
}
PageNavigationEntry_FriendPage.descriptor = new PageNavigationEntry_FriendPage_Descriptor();
class PageNavigationEntry_EventPage_Tab {
  static fromJson(json) {
    if (typeof json === "string") {
      switch (json) {
        case "INTRO": return 0;
        case "CHEST": return 1;
        case "LEVEL": return 2;
        case "REWARD": return 3;
        case "COOP": return 4;
        case "NORMAL_CHEST": return 5;
        default: return 0;
      }
    }
    return json;
  }
  static toJson(value) {
    return value;
  }
  static fromAny(value) {
    return PageNavigationEntry_EventPage_Tab.fromJson(value);
  }
  static fromReact(e) {
    return PageNavigationEntry_EventPage_Tab.fromJson(e);
  }
}
PageNavigationEntry_EventPage_Tab.INTRO = 0;
PageNavigationEntry_EventPage_Tab.CHEST = 1;
PageNavigationEntry_EventPage_Tab.LEVEL = 2;
PageNavigationEntry_EventPage_Tab.REWARD = 3;
PageNavigationEntry_EventPage_Tab.COOP = 4;
PageNavigationEntry_EventPage_Tab.NORMAL_CHEST = 5;
PageNavigationEntry_EventPage_Tab.default = 0;
PageNavigationEntry_EventPage_Tab.values = {
  INTRO: 0,
  CHEST: 1,
  LEVEL: 2,
  REWARD: 3,
  COOP: 4,
  NORMAL_CHEST: 5,
};
PageNavigationEntry_EventPage_Tab.names = {
  0: "INTRO",
  1: "CHEST",
  2: "LEVEL",
  3: "REWARD",
  4: "COOP",
  5: "NORMAL_CHEST",
};
class PageNavigationEntry_EventPage {
  get descriptor() {
    return PageNavigationEntry_EventPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasTab = false;
    this._tab = null;
  }
  isEmpty() {
    if (this._hasTab) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_EventPage) {
      return value;
    }
    return PageNavigationEntry_EventPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_EventPage();
    if (json.tab != null) {
      result._hasTab = true;
      result._tab = PageNavigationEntry_EventPage_Tab.fromJson(json.tab);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasTab) {
      result.tab = PageNavigationEntry_EventPage_Tab.toJson(this._tab);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get tab() {
    return this._hasTab ? this._tab : PageNavigationEntry_EventPage_Tab.default;
  }
  get hasTab() {
    return this._hasTab;
  }
  setTab(path, value) {
    return {type: 'SET', path: path.concat(['tab']), value};
  }
  deleteTab(path) {
    return {type: 'DELETE', path: path.concat(['tab'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "tab": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_EventPage(this);
              copy._hasTab = true;
              copy._tab = PageNavigationEntry_EventPage_Tab.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasTab) {
                return this;
              }
              const copy = new PageNavigationEntry_EventPage(this);
              copy._hasTab = false;
              copy._tab = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_EventPage} other
   * @returns {PageNavigationEntry_EventPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_EventPage(this);
    if (other._hasTab) {
      copy._hasTab = true;
      copy._tab = other._tab;
    }
    return copy;
  }
}
PageNavigationEntry_EventPage.default = new PageNavigationEntry_EventPage();
PageNavigationEntry_EventPage.Tab = PageNavigationEntry_EventPage_Tab;
class PageNavigationEntry_EventPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_EventPage(value);
  }
  build() {
    return new PageNavigationEntry_EventPage(this._value);
  }
  setTab(value) {
    this._value._hasTab = true;
    this._value._tab = value;
    return this;
  }
  deleteTab() {
    this._value._hasTab = false;
    this._value._tab = null;
    return this;
  }
}
PageNavigationEntry_EventPage.Builder = PageNavigationEntry_EventPage_Builder;
class PageNavigationEntry_EventPage_WithParent {
  /**
   * @param {PageNavigationEntry_EventPage} value
   * @param {PageNavigationEntry_EventPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_EventPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasTab() {
    if (this._cachedHasTab === undefined) {
      if (this._value.hasTab) {
        this._cachedHasTab = true;
      } else {
        this._cachedHasTab = this._parent ? this._parent.hasTab : false;
      }
    }
    return this._cachedHasTab;
  }
  get tab() {
    if (this._cachedTab === undefined) {
      if (this._value.hasTab) {
        this._cachedTab = this._value.tab;
      } else {
        this._cachedTab = this._parent ? this._parent.tab : this._value.tab;
      }
    }
    return this._cachedTab;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_EventPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_EventPage_WithParent.default = new PageNavigationEntry_EventPage_WithParent(PageNavigationEntry_EventPage.default, null);
PageNavigationEntry_EventPage.WithParent = PageNavigationEntry_EventPage_WithParent;
class PageNavigationEntry_EventPage_Descriptor {
  get tab() {
    if (!this._tab) {
      this._tab = {
        name: "tab",
        Name: "Tab",
        number: 1,
        protoType: 14,
        protoName: "tab",
        type: PageNavigationEntry_EventPage_Tab,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._tab;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.tab,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForTab",
        Name: "DummyOneofForTab",
        fields: [
          this.fields[0],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_EventPage.descriptor = new PageNavigationEntry_EventPage_Descriptor();
class PageNavigationEntry_GuildPage {
  get descriptor() {
    return PageNavigationEntry_GuildPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
  }
  isEmpty() {
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_GuildPage) {
      return value;
    }
    return PageNavigationEntry_GuildPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_GuildPage();
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    return result;
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_GuildPage} other
   * @returns {PageNavigationEntry_GuildPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_GuildPage(this);
    return copy;
  }
}
PageNavigationEntry_GuildPage.default = new PageNavigationEntry_GuildPage();
class PageNavigationEntry_GuildPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_GuildPage(value);
  }
  build() {
    return new PageNavigationEntry_GuildPage(this._value);
  }
}
PageNavigationEntry_GuildPage.Builder = PageNavigationEntry_GuildPage_Builder;
class PageNavigationEntry_GuildPage_WithParent {
  /**
   * @param {PageNavigationEntry_GuildPage} value
   * @param {PageNavigationEntry_GuildPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_GuildPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_GuildPage_WithParent(current, parent);
    return result;
  }
}
PageNavigationEntry_GuildPage_WithParent.default = new PageNavigationEntry_GuildPage_WithParent(PageNavigationEntry_GuildPage.default, null);
PageNavigationEntry_GuildPage.WithParent = PageNavigationEntry_GuildPage_WithParent;
class PageNavigationEntry_GuildPage_Descriptor {
  get fields() {
    if (!this._fields) {
      this._fields = [
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
    }
    return this._oneofs;
  }
}
PageNavigationEntry_GuildPage.descriptor = new PageNavigationEntry_GuildPage_Descriptor();
class PageNavigationEntry_ItemNavigationPage {
  get descriptor() {
    return PageNavigationEntry_ItemNavigationPage.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasItemKey = false;
    this._itemKey = null;
    this._hasTitle = false;
    this._title = null;
    this._hasDescription = false;
    this._description = null;
    this._hasGoButtonNavigation = false;
    this._goButtonNavigation = null;
  }
  isEmpty() {
    if (this._hasItemKey) return false;
    if (this._hasTitle) return false;
    if (this._hasDescription) return false;
    if (this._hasGoButtonNavigation && !this._goButtonNavigation.isEmpty()) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry_ItemNavigationPage) {
      return value;
    }
    return PageNavigationEntry_ItemNavigationPage.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry_ItemNavigationPage();
    if (json.itemKey != null) {
      result._hasItemKey = true;
      result._itemKey = JsonToString.fromJson(json.itemKey);
    }
    if (json.title != null) {
      result._hasTitle = true;
      result._title = JsonToString.fromJson(json.title);
    }
    if (json.description != null) {
      result._hasDescription = true;
      result._description = JsonToString.fromJson(json.description);
    }
    if (json.goButtonNavigation != null) {
      result._hasGoButtonNavigation = true;
      result._goButtonNavigation = PageNavigationEntry.fromJson(json.goButtonNavigation);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasItemKey) {
      result.itemKey = JsonToString.toJson(this._itemKey);
    }
    if (this._hasTitle) {
      result.title = JsonToString.toJson(this._title);
    }
    if (this._hasDescription) {
      result.description = JsonToString.toJson(this._description);
    }
    if (this._hasGoButtonNavigation && !this._goButtonNavigation.isEmpty()) {
      result.goButtonNavigation = PageNavigationEntry.toJson(this._goButtonNavigation);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get itemKey() {
    return this._hasItemKey ? this._itemKey : JsonToString.default;
  }
  get hasItemKey() {
    return this._hasItemKey;
  }
  setItemKey(path, value) {
    return {type: 'SET', path: path.concat(['itemKey']), value};
  }
  deleteItemKey(path) {
    return {type: 'DELETE', path: path.concat(['itemKey'])};
  }
  /**
   * @returns {string}
   */
  get title() {
    return this._hasTitle ? this._title : JsonToString.default;
  }
  get hasTitle() {
    return this._hasTitle;
  }
  setTitle(path, value) {
    return {type: 'SET', path: path.concat(['title']), value};
  }
  deleteTitle(path) {
    return {type: 'DELETE', path: path.concat(['title'])};
  }
  /**
   * @returns {string}
   */
  get description() {
    return this._hasDescription ? this._description : JsonToString.default;
  }
  get hasDescription() {
    return this._hasDescription;
  }
  setDescription(path, value) {
    return {type: 'SET', path: path.concat(['description']), value};
  }
  deleteDescription(path) {
    return {type: 'DELETE', path: path.concat(['description'])};
  }
  /**
   * @returns {PageNavigationEntry}
   */
  get goButtonNavigation() {
    return this._hasGoButtonNavigation ? this._goButtonNavigation : PageNavigationEntry.default;
  }
  get hasGoButtonNavigation() {
    return this._hasGoButtonNavigation;
  }
  setGoButtonNavigation(path, value) {
    return {type: 'SET', path: path.concat(['goButtonNavigation']), value: value.toJson()};
  }
  deleteGoButtonNavigation(path) {
    return {type: 'DELETE', path: path.concat(['goButtonNavigation'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "itemKey": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_ItemNavigationPage(this);
              copy._hasItemKey = true;
              copy._itemKey = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasItemKey) {
                return this;
              }
              const copy = new PageNavigationEntry_ItemNavigationPage(this);
              copy._hasItemKey = false;
              copy._itemKey = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "title": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_ItemNavigationPage(this);
              copy._hasTitle = true;
              copy._title = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasTitle) {
                return this;
              }
              const copy = new PageNavigationEntry_ItemNavigationPage(this);
              copy._hasTitle = false;
              copy._title = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "description": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_ItemNavigationPage(this);
              copy._hasDescription = true;
              copy._description = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasDescription) {
                return this;
              }
              const copy = new PageNavigationEntry_ItemNavigationPage(this);
              copy._hasDescription = false;
              copy._description = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "goButtonNavigation": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry_ItemNavigationPage(this);
              copy._hasGoButtonNavigation = true;
              copy._goButtonNavigation = PageNavigationEntry.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasGoButtonNavigation) {
                return this;
              }
              const copy = new PageNavigationEntry_ItemNavigationPage(this);
              copy._hasGoButtonNavigation = false;
              copy._goButtonNavigation = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (!this._hasGoButtonNavigation) {
            const copy = new PageNavigationEntry_ItemNavigationPage(this);
            copy._hasGoButtonNavigation = true;
            copy._goButtonNavigation = PageNavigationEntry.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this._goButtonNavigation.apply(type, path.slice(1), value);
          if (newValue === this._goButtonNavigation) {
            return this;
          }
          const copy = new PageNavigationEntry_ItemNavigationPage(this);
          copy._goButtonNavigation = copy._goButtonNavigation.apply(type, path.slice(1), value);
          return copy;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry_ItemNavigationPage} other
   * @returns {PageNavigationEntry_ItemNavigationPage}
   */
  merge(other) {
    const copy = new PageNavigationEntry_ItemNavigationPage(this);
    if (other._hasItemKey) {
      copy._hasItemKey = true;
      copy._itemKey = other._itemKey;
    }
    if (other._hasTitle) {
      copy._hasTitle = true;
      copy._title = other._title;
    }
    if (other._hasDescription) {
      copy._hasDescription = true;
      copy._description = other._description;
    }
    if (other._hasGoButtonNavigation) {
      if (copy._hasGoButtonNavigation) {
        copy._goButtonNavigation = copy.goButtonNavigation.merge(other.goButtonNavigation);
      } else {
        copy._goButtonNavigation = other._goButtonNavigation;
      }
      copy._hasGoButtonNavigation = true;
    }
    return copy;
  }
}
PageNavigationEntry_ItemNavigationPage.default = new PageNavigationEntry_ItemNavigationPage();
class PageNavigationEntry_ItemNavigationPage_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry_ItemNavigationPage(value);
  }
  build() {
    return new PageNavigationEntry_ItemNavigationPage(this._value);
  }
  setItemKey(value) {
    this._value._hasItemKey = true;
    this._value._itemKey = value;
    return this;
  }
  deleteItemKey() {
    this._value._hasItemKey = false;
    this._value._itemKey = null;
    return this;
  }
  setTitle(value) {
    this._value._hasTitle = true;
    this._value._title = value;
    return this;
  }
  deleteTitle() {
    this._value._hasTitle = false;
    this._value._title = null;
    return this;
  }
  setDescription(value) {
    this._value._hasDescription = true;
    this._value._description = value;
    return this;
  }
  deleteDescription() {
    this._value._hasDescription = false;
    this._value._description = null;
    return this;
  }
  setGoButtonNavigation(value) {
    this._value._hasGoButtonNavigation = true;
    this._value._goButtonNavigation = value;
    return this;
  }
  deleteGoButtonNavigation() {
    this._value._hasGoButtonNavigation = false;
    this._value._goButtonNavigation = null;
    return this;
  }
}
PageNavigationEntry_ItemNavigationPage.Builder = PageNavigationEntry_ItemNavigationPage_Builder;
class PageNavigationEntry_ItemNavigationPage_WithParent {
  /**
   * @param {PageNavigationEntry_ItemNavigationPage} value
   * @param {PageNavigationEntry_ItemNavigationPage_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry_ItemNavigationPage.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasItemKey() {
    if (this._cachedHasItemKey === undefined) {
      if (this._value.hasItemKey) {
        this._cachedHasItemKey = true;
      } else {
        this._cachedHasItemKey = this._parent ? this._parent.hasItemKey : false;
      }
    }
    return this._cachedHasItemKey;
  }
  get itemKey() {
    if (this._cachedItemKey === undefined) {
      if (this._value.hasItemKey) {
        this._cachedItemKey = this._value.itemKey;
      } else {
        this._cachedItemKey = this._parent ? this._parent.itemKey : this._value.itemKey;
      }
    }
    return this._cachedItemKey;
  }
  get hasTitle() {
    if (this._cachedHasTitle === undefined) {
      if (this._value.hasTitle) {
        this._cachedHasTitle = true;
      } else {
        this._cachedHasTitle = this._parent ? this._parent.hasTitle : false;
      }
    }
    return this._cachedHasTitle;
  }
  get title() {
    if (this._cachedTitle === undefined) {
      if (this._value.hasTitle) {
        this._cachedTitle = this._value.title;
      } else {
        this._cachedTitle = this._parent ? this._parent.title : this._value.title;
      }
    }
    return this._cachedTitle;
  }
  get hasDescription() {
    if (this._cachedHasDescription === undefined) {
      if (this._value.hasDescription) {
        this._cachedHasDescription = true;
      } else {
        this._cachedHasDescription = this._parent ? this._parent.hasDescription : false;
      }
    }
    return this._cachedHasDescription;
  }
  get description() {
    if (this._cachedDescription === undefined) {
      if (this._value.hasDescription) {
        this._cachedDescription = this._value.description;
      } else {
        this._cachedDescription = this._parent ? this._parent.description : this._value.description;
      }
    }
    return this._cachedDescription;
  }
  get hasGoButtonNavigation() {
    if (this._cachedHasGoButtonNavigation === undefined) {
      if (this._value.hasGoButtonNavigation) {
        this._cachedHasGoButtonNavigation = true;
      } else {
        this._cachedHasGoButtonNavigation = this._parent ? this._parent.hasGoButtonNavigation : false;
      }
    }
    return this._cachedHasGoButtonNavigation;
  }
  get goButtonNavigation() {
    if (!this._cachedGoButtonNavigation) {
      if (this._value.hasGoButtonNavigation) {
        this._cachedGoButtonNavigation = new PageNavigationEntry.WithParent(this._value.goButtonNavigation, this._parent && this._parent.hasGoButtonNavigation ? this._parent.goButtonNavigation : null);
      } else {
        this._cachedGoButtonNavigation = this._parent ? new PageNavigationEntry.WithParent(PageNavigationEntry.default, this._parent.goButtonNavigation) : PageNavigationEntry.WithParent.default;
      }
    }
    return this._cachedGoButtonNavigation;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_ItemNavigationPage_WithParent(current, parent);
    if (this._cachedGoButtonNavigation && this._value.hasGoButtonNavigation && current.hasGoButtonNavigation) {
      if (this._value.goButtonNavigation === current.goButtonNavigation && this._parent === parent) {
        result._cachedGoButtonNavigation = this._cachedGoButtonNavigation;
      } else {
        result._cachedGoButtonNavigation = this._cachedGoButtonNavigation.update(current.goButtonNavigation, parent && parent.hasGoButtonNavigation ? parent.goButtonNavigation : null);
      }
    }
    return result;
  }
}
PageNavigationEntry_ItemNavigationPage_WithParent.default = new PageNavigationEntry_ItemNavigationPage_WithParent(PageNavigationEntry_ItemNavigationPage.default, null);
PageNavigationEntry_ItemNavigationPage.WithParent = PageNavigationEntry_ItemNavigationPage_WithParent;
class PageNavigationEntry_ItemNavigationPage_Descriptor {
  get itemKey() {
    if (!this._itemKey) {
      this._itemKey = {
        name: "itemKey",
        Name: "ItemKey",
        number: 1,
        protoType: 9,
        protoName: "item_key",
        type: JsonToString,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._itemKey;
  }
  get title() {
    if (!this._title) {
      this._title = {
        name: "title",
        Name: "Title",
        number: 2,
        protoType: 9,
        protoName: "title",
        type: JsonToString,
        options: {
          localization: true,
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._title;
  }
  get description() {
    if (!this._description) {
      this._description = {
        name: "description",
        Name: "Description",
        number: 3,
        protoType: 9,
        protoName: "description",
        type: JsonToString,
        options: {
          localization: true,
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._description;
  }
  get goButtonNavigation() {
    if (!this._goButtonNavigation) {
      this._goButtonNavigation = {
        name: "goButtonNavigation",
        Name: "GoButtonNavigation",
        number: 4,
        protoType: 11,
        protoName: "go_button_navigation",
        type: PageNavigationEntry,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._goButtonNavigation;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.itemKey,
        this.title,
        this.description,
        this.goButtonNavigation,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForItemKey",
        Name: "DummyOneofForItemKey",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForTitle",
        Name: "DummyOneofForTitle",
        fields: [
          this.fields[1],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForDescription",
        Name: "DummyOneofForDescription",
        fields: [
          this.fields[2],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForGoButtonNavigation",
        Name: "DummyOneofForGoButtonNavigation",
        fields: [
          this.fields[3],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry_ItemNavigationPage.descriptor = new PageNavigationEntry_ItemNavigationPage_Descriptor();
export class PageNavigationEntry {
  get descriptor() {
    return PageNavigationEntry.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._entryCase = 0;
    this._entryValue = null;
  }
  isEmpty() {
    if (this._entryCase !== 0) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof PageNavigationEntry) {
      return value;
    }
    return PageNavigationEntry.fromJson(value);
  }
  static fromJson(json) {
    const result = new PageNavigationEntry();
    if (json.heroCardPage != null) {
      result._entryCase = 11;
      result._entryValue = PageNavigationEntry_HeroCardPage.fromJson(json.heroCardPage);
    }
    if (json.cranePage != null) {
      result._entryCase = 12;
      result._entryValue = PageNavigationEntry_CranePage.fromJson(json.cranePage);
    }
    if (json.portalPage != null) {
      result._entryCase = 13;
      result._entryValue = PageNavigationEntry_PortalPage.fromJson(json.portalPage);
    }
    if (json.minePage != null) {
      result._entryCase = 14;
      result._entryValue = PageNavigationEntry_MinePage.fromJson(json.minePage);
    }
    if (json.marketPage != null) {
      result._entryCase = 15;
      result._entryValue = PageNavigationEntry_MarketPage.fromJson(json.marketPage);
    }
    if (json.shopPage != null) {
      result._entryCase = 16;
      result._entryValue = PageNavigationEntry_ShopPage.fromJson(json.shopPage);
    }
    if (json.cardListPage != null) {
      result._entryCase = 17;
      result._entryValue = PageNavigationEntry_CardListPage.fromJson(json.cardListPage);
    }
    if (json.campaignPage != null) {
      result._entryCase = 18;
      result._entryValue = PageNavigationEntry_CampaignPage.fromJson(json.campaignPage);
    }
    if (json.facebookPage != null) {
      result._entryCase = 19;
      result._entryValue = PageNavigationEntry_FacebookPage.fromJson(json.facebookPage);
    }
    if (json.profilePage != null) {
      result._entryCase = 20;
      result._entryValue = PageNavigationEntry_ProfilePage.fromJson(json.profilePage);
    }
    if (json.website != null) {
      result._entryCase = 21;
      result._entryValue = PageNavigationEntry_Website.fromJson(json.website);
    }
    if (json.questPage != null) {
      result._entryCase = 22;
      result._entryValue = PageNavigationEntry_QuestPage.fromJson(json.questPage);
    }
    if (json.tournamentPage != null) {
      result._entryCase = 23;
      result._entryValue = PageNavigationEntry_TournamentPage.fromJson(json.tournamentPage);
    }
    if (json.eventPage != null) {
      result._entryCase = 24;
      result._entryValue = PageNavigationEntry_EventPage.fromJson(json.eventPage);
    }
    if (json.guildPage != null) {
      result._entryCase = 25;
      result._entryValue = PageNavigationEntry_GuildPage.fromJson(json.guildPage);
    }
    if (json.loadoutPage != null) {
      result._entryCase = 26;
      result._entryValue = PageNavigationEntry_LoadoutPage.fromJson(json.loadoutPage);
    }
    if (json.settingPage != null) {
      result._entryCase = 27;
      result._entryValue = PageNavigationEntry_SettingPage.fromJson(json.settingPage);
    }
    if (json.friendPage != null) {
      result._entryCase = 28;
      result._entryValue = PageNavigationEntry_FriendPage.fromJson(json.friendPage);
    }
    if (json.itemNavigationPage != null) {
      result._entryCase = 29;
      result._entryValue = PageNavigationEntry_ItemNavigationPage.fromJson(json.itemNavigationPage);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    switch (this._entryCase) {
      case 11:
        result.heroCardPage = PageNavigationEntry_HeroCardPage.toJson(this._entryValue);
        break;
      case 12:
        result.cranePage = PageNavigationEntry_CranePage.toJson(this._entryValue);
        break;
      case 13:
        result.portalPage = PageNavigationEntry_PortalPage.toJson(this._entryValue);
        break;
      case 14:
        result.minePage = PageNavigationEntry_MinePage.toJson(this._entryValue);
        break;
      case 15:
        result.marketPage = PageNavigationEntry_MarketPage.toJson(this._entryValue);
        break;
      case 16:
        result.shopPage = PageNavigationEntry_ShopPage.toJson(this._entryValue);
        break;
      case 17:
        result.cardListPage = PageNavigationEntry_CardListPage.toJson(this._entryValue);
        break;
      case 18:
        result.campaignPage = PageNavigationEntry_CampaignPage.toJson(this._entryValue);
        break;
      case 19:
        result.facebookPage = PageNavigationEntry_FacebookPage.toJson(this._entryValue);
        break;
      case 20:
        result.profilePage = PageNavigationEntry_ProfilePage.toJson(this._entryValue);
        break;
      case 21:
        result.website = PageNavigationEntry_Website.toJson(this._entryValue);
        break;
      case 22:
        result.questPage = PageNavigationEntry_QuestPage.toJson(this._entryValue);
        break;
      case 23:
        result.tournamentPage = PageNavigationEntry_TournamentPage.toJson(this._entryValue);
        break;
      case 24:
        result.eventPage = PageNavigationEntry_EventPage.toJson(this._entryValue);
        break;
      case 25:
        result.guildPage = PageNavigationEntry_GuildPage.toJson(this._entryValue);
        break;
      case 26:
        result.loadoutPage = PageNavigationEntry_LoadoutPage.toJson(this._entryValue);
        break;
      case 27:
        result.settingPage = PageNavigationEntry_SettingPage.toJson(this._entryValue);
        break;
      case 28:
        result.friendPage = PageNavigationEntry_FriendPage.toJson(this._entryValue);
        break;
      case 29:
        result.itemNavigationPage = PageNavigationEntry_ItemNavigationPage.toJson(this._entryValue);
        break;
      default:
        break;
    }
    return result;
  }
  get entryCase() {
    return this._entryCase;
  }
  deleteEntry(path) {
    switch (this._entryCase) {
      default:
      case 11: {
        return {type: 'DELETE', path: path.concat(['heroCardPage'])};
      }
      case 12: {
        return {type: 'DELETE', path: path.concat(['cranePage'])};
      }
      case 13: {
        return {type: 'DELETE', path: path.concat(['portalPage'])};
      }
      case 14: {
        return {type: 'DELETE', path: path.concat(['minePage'])};
      }
      case 15: {
        return {type: 'DELETE', path: path.concat(['marketPage'])};
      }
      case 16: {
        return {type: 'DELETE', path: path.concat(['shopPage'])};
      }
      case 17: {
        return {type: 'DELETE', path: path.concat(['cardListPage'])};
      }
      case 18: {
        return {type: 'DELETE', path: path.concat(['campaignPage'])};
      }
      case 19: {
        return {type: 'DELETE', path: path.concat(['facebookPage'])};
      }
      case 20: {
        return {type: 'DELETE', path: path.concat(['profilePage'])};
      }
      case 21: {
        return {type: 'DELETE', path: path.concat(['website'])};
      }
      case 22: {
        return {type: 'DELETE', path: path.concat(['questPage'])};
      }
      case 23: {
        return {type: 'DELETE', path: path.concat(['tournamentPage'])};
      }
      case 24: {
        return {type: 'DELETE', path: path.concat(['eventPage'])};
      }
      case 25: {
        return {type: 'DELETE', path: path.concat(['guildPage'])};
      }
      case 26: {
        return {type: 'DELETE', path: path.concat(['loadoutPage'])};
      }
      case 27: {
        return {type: 'DELETE', path: path.concat(['settingPage'])};
      }
      case 28: {
        return {type: 'DELETE', path: path.concat(['friendPage'])};
      }
      case 29: {
        return {type: 'DELETE', path: path.concat(['itemNavigationPage'])};
      }
    }
  }
  /**
   * @returns {PageNavigationEntry_HeroCardPage}
   */
  get heroCardPage() {
    return this._entryCase === 11 ? this._entryValue : PageNavigationEntry_HeroCardPage.default;
  }
  setHeroCardPage(path, value) {
    return {type: 'SET', path: path.concat(['heroCardPage']), value: value.toJson()};
  }
  deleteHeroCardPage(path) {
    return {type: 'DELETE', path: path.concat(['heroCardPage'])};
  }
  /**
   * @returns {PageNavigationEntry_CranePage}
   */
  get cranePage() {
    return this._entryCase === 12 ? this._entryValue : PageNavigationEntry_CranePage.default;
  }
  setCranePage(path, value) {
    return {type: 'SET', path: path.concat(['cranePage']), value: value.toJson()};
  }
  deleteCranePage(path) {
    return {type: 'DELETE', path: path.concat(['cranePage'])};
  }
  /**
   * @returns {PageNavigationEntry_PortalPage}
   */
  get portalPage() {
    return this._entryCase === 13 ? this._entryValue : PageNavigationEntry_PortalPage.default;
  }
  setPortalPage(path, value) {
    return {type: 'SET', path: path.concat(['portalPage']), value: value.toJson()};
  }
  deletePortalPage(path) {
    return {type: 'DELETE', path: path.concat(['portalPage'])};
  }
  /**
   * @returns {PageNavigationEntry_MinePage}
   */
  get minePage() {
    return this._entryCase === 14 ? this._entryValue : PageNavigationEntry_MinePage.default;
  }
  setMinePage(path, value) {
    return {type: 'SET', path: path.concat(['minePage']), value: value.toJson()};
  }
  deleteMinePage(path) {
    return {type: 'DELETE', path: path.concat(['minePage'])};
  }
  /**
   * @returns {PageNavigationEntry_MarketPage}
   */
  get marketPage() {
    return this._entryCase === 15 ? this._entryValue : PageNavigationEntry_MarketPage.default;
  }
  setMarketPage(path, value) {
    return {type: 'SET', path: path.concat(['marketPage']), value: value.toJson()};
  }
  deleteMarketPage(path) {
    return {type: 'DELETE', path: path.concat(['marketPage'])};
  }
  /**
   * @returns {PageNavigationEntry_ShopPage}
   */
  get shopPage() {
    return this._entryCase === 16 ? this._entryValue : PageNavigationEntry_ShopPage.default;
  }
  setShopPage(path, value) {
    return {type: 'SET', path: path.concat(['shopPage']), value: value.toJson()};
  }
  deleteShopPage(path) {
    return {type: 'DELETE', path: path.concat(['shopPage'])};
  }
  /**
   * @returns {PageNavigationEntry_CardListPage}
   */
  get cardListPage() {
    return this._entryCase === 17 ? this._entryValue : PageNavigationEntry_CardListPage.default;
  }
  setCardListPage(path, value) {
    return {type: 'SET', path: path.concat(['cardListPage']), value: value.toJson()};
  }
  deleteCardListPage(path) {
    return {type: 'DELETE', path: path.concat(['cardListPage'])};
  }
  /**
   * @returns {PageNavigationEntry_CampaignPage}
   */
  get campaignPage() {
    return this._entryCase === 18 ? this._entryValue : PageNavigationEntry_CampaignPage.default;
  }
  setCampaignPage(path, value) {
    return {type: 'SET', path: path.concat(['campaignPage']), value: value.toJson()};
  }
  deleteCampaignPage(path) {
    return {type: 'DELETE', path: path.concat(['campaignPage'])};
  }
  /**
   * @returns {PageNavigationEntry_FacebookPage}
   */
  get facebookPage() {
    return this._entryCase === 19 ? this._entryValue : PageNavigationEntry_FacebookPage.default;
  }
  setFacebookPage(path, value) {
    return {type: 'SET', path: path.concat(['facebookPage']), value: value.toJson()};
  }
  deleteFacebookPage(path) {
    return {type: 'DELETE', path: path.concat(['facebookPage'])};
  }
  /**
   * @returns {PageNavigationEntry_ProfilePage}
   */
  get profilePage() {
    return this._entryCase === 20 ? this._entryValue : PageNavigationEntry_ProfilePage.default;
  }
  setProfilePage(path, value) {
    return {type: 'SET', path: path.concat(['profilePage']), value: value.toJson()};
  }
  deleteProfilePage(path) {
    return {type: 'DELETE', path: path.concat(['profilePage'])};
  }
  /**
   * @returns {PageNavigationEntry_Website}
   */
  get website() {
    return this._entryCase === 21 ? this._entryValue : PageNavigationEntry_Website.default;
  }
  setWebsite(path, value) {
    return {type: 'SET', path: path.concat(['website']), value: value.toJson()};
  }
  deleteWebsite(path) {
    return {type: 'DELETE', path: path.concat(['website'])};
  }
  /**
   * @returns {PageNavigationEntry_QuestPage}
   */
  get questPage() {
    return this._entryCase === 22 ? this._entryValue : PageNavigationEntry_QuestPage.default;
  }
  setQuestPage(path, value) {
    return {type: 'SET', path: path.concat(['questPage']), value: value.toJson()};
  }
  deleteQuestPage(path) {
    return {type: 'DELETE', path: path.concat(['questPage'])};
  }
  /**
   * @returns {PageNavigationEntry_TournamentPage}
   */
  get tournamentPage() {
    return this._entryCase === 23 ? this._entryValue : PageNavigationEntry_TournamentPage.default;
  }
  setTournamentPage(path, value) {
    return {type: 'SET', path: path.concat(['tournamentPage']), value: value.toJson()};
  }
  deleteTournamentPage(path) {
    return {type: 'DELETE', path: path.concat(['tournamentPage'])};
  }
  /**
   * @returns {PageNavigationEntry_EventPage}
   */
  get eventPage() {
    return this._entryCase === 24 ? this._entryValue : PageNavigationEntry_EventPage.default;
  }
  setEventPage(path, value) {
    return {type: 'SET', path: path.concat(['eventPage']), value: value.toJson()};
  }
  deleteEventPage(path) {
    return {type: 'DELETE', path: path.concat(['eventPage'])};
  }
  /**
   * @returns {PageNavigationEntry_GuildPage}
   */
  get guildPage() {
    return this._entryCase === 25 ? this._entryValue : PageNavigationEntry_GuildPage.default;
  }
  setGuildPage(path, value) {
    return {type: 'SET', path: path.concat(['guildPage']), value: value.toJson()};
  }
  deleteGuildPage(path) {
    return {type: 'DELETE', path: path.concat(['guildPage'])};
  }
  /**
   * @returns {PageNavigationEntry_LoadoutPage}
   */
  get loadoutPage() {
    return this._entryCase === 26 ? this._entryValue : PageNavigationEntry_LoadoutPage.default;
  }
  setLoadoutPage(path, value) {
    return {type: 'SET', path: path.concat(['loadoutPage']), value: value.toJson()};
  }
  deleteLoadoutPage(path) {
    return {type: 'DELETE', path: path.concat(['loadoutPage'])};
  }
  /**
   * @returns {PageNavigationEntry_SettingPage}
   */
  get settingPage() {
    return this._entryCase === 27 ? this._entryValue : PageNavigationEntry_SettingPage.default;
  }
  setSettingPage(path, value) {
    return {type: 'SET', path: path.concat(['settingPage']), value: value.toJson()};
  }
  deleteSettingPage(path) {
    return {type: 'DELETE', path: path.concat(['settingPage'])};
  }
  /**
   * @returns {PageNavigationEntry_FriendPage}
   */
  get friendPage() {
    return this._entryCase === 28 ? this._entryValue : PageNavigationEntry_FriendPage.default;
  }
  setFriendPage(path, value) {
    return {type: 'SET', path: path.concat(['friendPage']), value: value.toJson()};
  }
  deleteFriendPage(path) {
    return {type: 'DELETE', path: path.concat(['friendPage'])};
  }
  /**
   * @returns {PageNavigationEntry_ItemNavigationPage}
   */
  get itemNavigationPage() {
    return this._entryCase === 29 ? this._entryValue : PageNavigationEntry_ItemNavigationPage.default;
  }
  setItemNavigationPage(path, value) {
    return {type: 'SET', path: path.concat(['itemNavigationPage']), value: value.toJson()};
  }
  deleteItemNavigationPage(path) {
    return {type: 'DELETE', path: path.concat(['itemNavigationPage'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "heroCardPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 11;
              copy._entryValue = PageNavigationEntry_HeroCardPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 11) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 11) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 11;
            copy._entryValue = PageNavigationEntry_HeroCardPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.heroCardPage.apply(type, path.slice(1), value);
          if (newValue === this.heroCardPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 11;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "cranePage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 12;
              copy._entryValue = PageNavigationEntry_CranePage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 12) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 12) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 12;
            copy._entryValue = PageNavigationEntry_CranePage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.cranePage.apply(type, path.slice(1), value);
          if (newValue === this.cranePage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 12;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "portalPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 13;
              copy._entryValue = PageNavigationEntry_PortalPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 13) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 13) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 13;
            copy._entryValue = PageNavigationEntry_PortalPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.portalPage.apply(type, path.slice(1), value);
          if (newValue === this.portalPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 13;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "minePage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 14;
              copy._entryValue = PageNavigationEntry_MinePage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 14) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 14) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 14;
            copy._entryValue = PageNavigationEntry_MinePage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.minePage.apply(type, path.slice(1), value);
          if (newValue === this.minePage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 14;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "marketPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 15;
              copy._entryValue = PageNavigationEntry_MarketPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 15) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 15) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 15;
            copy._entryValue = PageNavigationEntry_MarketPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.marketPage.apply(type, path.slice(1), value);
          if (newValue === this.marketPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 15;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "shopPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 16;
              copy._entryValue = PageNavigationEntry_ShopPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 16) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 16) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 16;
            copy._entryValue = PageNavigationEntry_ShopPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.shopPage.apply(type, path.slice(1), value);
          if (newValue === this.shopPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 16;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "cardListPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 17;
              copy._entryValue = PageNavigationEntry_CardListPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 17) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 17) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 17;
            copy._entryValue = PageNavigationEntry_CardListPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.cardListPage.apply(type, path.slice(1), value);
          if (newValue === this.cardListPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 17;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "campaignPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 18;
              copy._entryValue = PageNavigationEntry_CampaignPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 18) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 18) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 18;
            copy._entryValue = PageNavigationEntry_CampaignPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.campaignPage.apply(type, path.slice(1), value);
          if (newValue === this.campaignPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 18;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "facebookPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 19;
              copy._entryValue = PageNavigationEntry_FacebookPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 19) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 19) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 19;
            copy._entryValue = PageNavigationEntry_FacebookPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.facebookPage.apply(type, path.slice(1), value);
          if (newValue === this.facebookPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 19;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "profilePage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 20;
              copy._entryValue = PageNavigationEntry_ProfilePage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 20) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 20) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 20;
            copy._entryValue = PageNavigationEntry_ProfilePage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.profilePage.apply(type, path.slice(1), value);
          if (newValue === this.profilePage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 20;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "website": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 21;
              copy._entryValue = PageNavigationEntry_Website.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 21) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 21) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 21;
            copy._entryValue = PageNavigationEntry_Website.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.website.apply(type, path.slice(1), value);
          if (newValue === this.website) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 21;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "questPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 22;
              copy._entryValue = PageNavigationEntry_QuestPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 22) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 22) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 22;
            copy._entryValue = PageNavigationEntry_QuestPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.questPage.apply(type, path.slice(1), value);
          if (newValue === this.questPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 22;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "tournamentPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 23;
              copy._entryValue = PageNavigationEntry_TournamentPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 23) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 23) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 23;
            copy._entryValue = PageNavigationEntry_TournamentPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.tournamentPage.apply(type, path.slice(1), value);
          if (newValue === this.tournamentPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 23;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "eventPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 24;
              copy._entryValue = PageNavigationEntry_EventPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 24) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 24) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 24;
            copy._entryValue = PageNavigationEntry_EventPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.eventPage.apply(type, path.slice(1), value);
          if (newValue === this.eventPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 24;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "guildPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 25;
              copy._entryValue = PageNavigationEntry_GuildPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 25) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 25) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 25;
            copy._entryValue = PageNavigationEntry_GuildPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.guildPage.apply(type, path.slice(1), value);
          if (newValue === this.guildPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 25;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "loadoutPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 26;
              copy._entryValue = PageNavigationEntry_LoadoutPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 26) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 26) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 26;
            copy._entryValue = PageNavigationEntry_LoadoutPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.loadoutPage.apply(type, path.slice(1), value);
          if (newValue === this.loadoutPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 26;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "settingPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 27;
              copy._entryValue = PageNavigationEntry_SettingPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 27) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 27) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 27;
            copy._entryValue = PageNavigationEntry_SettingPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.settingPage.apply(type, path.slice(1), value);
          if (newValue === this.settingPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 27;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "friendPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 28;
              copy._entryValue = PageNavigationEntry_FriendPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 28) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 28) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 28;
            copy._entryValue = PageNavigationEntry_FriendPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.friendPage.apply(type, path.slice(1), value);
          if (newValue === this.friendPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 28;
          copy._entryValue = newValue;
          return copy;
        }
      }
      case "itemNavigationPage": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 29;
              copy._entryValue = PageNavigationEntry_ItemNavigationPage.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (this._entryCase !== 29) {
                return this;
              }
              const copy = new PageNavigationEntry(this);
              copy._entryCase = 0;
              copy._entryValue = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (this._entryCase !== 29) {
            const copy = new PageNavigationEntry(this);
            copy._entryCase = 29;
            copy._entryValue = PageNavigationEntry_ItemNavigationPage.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this.itemNavigationPage.apply(type, path.slice(1), value);
          if (newValue === this.itemNavigationPage) {
            return this;
          }
          const copy = new PageNavigationEntry(this);
          copy._entryCase = 29;
          copy._entryValue = newValue;
          return copy;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {PageNavigationEntry} other
   * @returns {PageNavigationEntry}
   */
  merge(other) {
    const copy = new PageNavigationEntry(this);
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    if (other._entryCase === 11) {
      if (this._entryCase === 11) {
        copy._entryValue = copy._entryValue.merge(other._entryValue);
      } else {
        copy._entryValue = other._entryValue;
      }
      copy._entryCase = 11;
    }
    return copy;
  }
}
PageNavigationEntry.default = new PageNavigationEntry();
PageNavigationEntry.HeroCardPage = PageNavigationEntry_HeroCardPage;
PageNavigationEntry.CranePage = PageNavigationEntry_CranePage;
PageNavigationEntry.PortalPage = PageNavigationEntry_PortalPage;
PageNavigationEntry.MinePage = PageNavigationEntry_MinePage;
PageNavigationEntry.MarketPage = PageNavigationEntry_MarketPage;
PageNavigationEntry.ShopPage = PageNavigationEntry_ShopPage;
PageNavigationEntry.CardListPage = PageNavigationEntry_CardListPage;
PageNavigationEntry.CampaignPage = PageNavigationEntry_CampaignPage;
PageNavigationEntry.LoadoutPage = PageNavigationEntry_LoadoutPage;
PageNavigationEntry.FacebookPage = PageNavigationEntry_FacebookPage;
PageNavigationEntry.QuestPage = PageNavigationEntry_QuestPage;
PageNavigationEntry.Website = PageNavigationEntry_Website;
PageNavigationEntry.ProfilePage = PageNavigationEntry_ProfilePage;
PageNavigationEntry.SettingPage = PageNavigationEntry_SettingPage;
PageNavigationEntry.TournamentPage = PageNavigationEntry_TournamentPage;
PageNavigationEntry.FriendPage = PageNavigationEntry_FriendPage;
PageNavigationEntry.EventPage = PageNavigationEntry_EventPage;
PageNavigationEntry.GuildPage = PageNavigationEntry_GuildPage;
PageNavigationEntry.ItemNavigationPage = PageNavigationEntry_ItemNavigationPage;
class PageNavigationEntry_Builder {
  constructor(value) {
    this._value = new PageNavigationEntry(value);
  }
  build() {
    return new PageNavigationEntry(this._value);
  }
  clearentry() {
    this._value._entryCase = 0;
    this._value._entryValue = null;
    return this;
  }
  setHeroCardPage(value) {
    this._value._entryCase = 11;
    this._value._entryValue = value;
    return this;
  }
  setCranePage(value) {
    this._value._entryCase = 12;
    this._value._entryValue = value;
    return this;
  }
  setPortalPage(value) {
    this._value._entryCase = 13;
    this._value._entryValue = value;
    return this;
  }
  setMinePage(value) {
    this._value._entryCase = 14;
    this._value._entryValue = value;
    return this;
  }
  setMarketPage(value) {
    this._value._entryCase = 15;
    this._value._entryValue = value;
    return this;
  }
  setShopPage(value) {
    this._value._entryCase = 16;
    this._value._entryValue = value;
    return this;
  }
  setCardListPage(value) {
    this._value._entryCase = 17;
    this._value._entryValue = value;
    return this;
  }
  setCampaignPage(value) {
    this._value._entryCase = 18;
    this._value._entryValue = value;
    return this;
  }
  setFacebookPage(value) {
    this._value._entryCase = 19;
    this._value._entryValue = value;
    return this;
  }
  setProfilePage(value) {
    this._value._entryCase = 20;
    this._value._entryValue = value;
    return this;
  }
  setWebsite(value) {
    this._value._entryCase = 21;
    this._value._entryValue = value;
    return this;
  }
  setQuestPage(value) {
    this._value._entryCase = 22;
    this._value._entryValue = value;
    return this;
  }
  setTournamentPage(value) {
    this._value._entryCase = 23;
    this._value._entryValue = value;
    return this;
  }
  setEventPage(value) {
    this._value._entryCase = 24;
    this._value._entryValue = value;
    return this;
  }
  setGuildPage(value) {
    this._value._entryCase = 25;
    this._value._entryValue = value;
    return this;
  }
  setLoadoutPage(value) {
    this._value._entryCase = 26;
    this._value._entryValue = value;
    return this;
  }
  setSettingPage(value) {
    this._value._entryCase = 27;
    this._value._entryValue = value;
    return this;
  }
  setFriendPage(value) {
    this._value._entryCase = 28;
    this._value._entryValue = value;
    return this;
  }
  setItemNavigationPage(value) {
    this._value._entryCase = 29;
    this._value._entryValue = value;
    return this;
  }
}
PageNavigationEntry.Builder = PageNavigationEntry_Builder;
class PageNavigationEntry_WithParent {
  /**
   * @param {PageNavigationEntry} value
   * @param {PageNavigationEntry_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return PageNavigationEntry.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get entryCase() {
    if (this._cachedentryCase === undefined) {
      if (this._value._entryCase === 0) {
        this._cachedentryCase = this._parent ? this._parent.entryCase : 0;
      } else {
        this._cachedentryCase = this._value._entryCase;
      }
    }
    return this._cachedentryCase;
  }
  get heroCardPage() {
    if (!this._cachedHeroCardPage) {
      if (this._value._entryCase === 0) {
        this._cachedHeroCardPage = this._parent ? new PageNavigationEntry_HeroCardPage.WithParent(PageNavigationEntry_HeroCardPage.default, this._parent.heroCardPage) : this._value.heroCardPage;
      } else if (this._value._entryCase === 11) {
        this._cachedHeroCardPage = new PageNavigationEntry_HeroCardPage.WithParent(this._value.heroCardPage, this._parent && this._parent.entryCase === 11 ? this._parent.heroCardPage : null);
      } else {
        this._cachedHeroCardPage = PageNavigationEntry_HeroCardPage.WithParent.default;
      }
    }
    return this._cachedHeroCardPage;
  }
  get cranePage() {
    if (!this._cachedCranePage) {
      if (this._value._entryCase === 0) {
        this._cachedCranePage = this._parent ? new PageNavigationEntry_CranePage.WithParent(PageNavigationEntry_CranePage.default, this._parent.cranePage) : this._value.cranePage;
      } else if (this._value._entryCase === 12) {
        this._cachedCranePage = new PageNavigationEntry_CranePage.WithParent(this._value.cranePage, this._parent && this._parent.entryCase === 12 ? this._parent.cranePage : null);
      } else {
        this._cachedCranePage = PageNavigationEntry_CranePage.WithParent.default;
      }
    }
    return this._cachedCranePage;
  }
  get portalPage() {
    if (!this._cachedPortalPage) {
      if (this._value._entryCase === 0) {
        this._cachedPortalPage = this._parent ? new PageNavigationEntry_PortalPage.WithParent(PageNavigationEntry_PortalPage.default, this._parent.portalPage) : this._value.portalPage;
      } else if (this._value._entryCase === 13) {
        this._cachedPortalPage = new PageNavigationEntry_PortalPage.WithParent(this._value.portalPage, this._parent && this._parent.entryCase === 13 ? this._parent.portalPage : null);
      } else {
        this._cachedPortalPage = PageNavigationEntry_PortalPage.WithParent.default;
      }
    }
    return this._cachedPortalPage;
  }
  get minePage() {
    if (!this._cachedMinePage) {
      if (this._value._entryCase === 0) {
        this._cachedMinePage = this._parent ? new PageNavigationEntry_MinePage.WithParent(PageNavigationEntry_MinePage.default, this._parent.minePage) : this._value.minePage;
      } else if (this._value._entryCase === 14) {
        this._cachedMinePage = new PageNavigationEntry_MinePage.WithParent(this._value.minePage, this._parent && this._parent.entryCase === 14 ? this._parent.minePage : null);
      } else {
        this._cachedMinePage = PageNavigationEntry_MinePage.WithParent.default;
      }
    }
    return this._cachedMinePage;
  }
  get marketPage() {
    if (!this._cachedMarketPage) {
      if (this._value._entryCase === 0) {
        this._cachedMarketPage = this._parent ? new PageNavigationEntry_MarketPage.WithParent(PageNavigationEntry_MarketPage.default, this._parent.marketPage) : this._value.marketPage;
      } else if (this._value._entryCase === 15) {
        this._cachedMarketPage = new PageNavigationEntry_MarketPage.WithParent(this._value.marketPage, this._parent && this._parent.entryCase === 15 ? this._parent.marketPage : null);
      } else {
        this._cachedMarketPage = PageNavigationEntry_MarketPage.WithParent.default;
      }
    }
    return this._cachedMarketPage;
  }
  get shopPage() {
    if (!this._cachedShopPage) {
      if (this._value._entryCase === 0) {
        this._cachedShopPage = this._parent ? new PageNavigationEntry_ShopPage.WithParent(PageNavigationEntry_ShopPage.default, this._parent.shopPage) : this._value.shopPage;
      } else if (this._value._entryCase === 16) {
        this._cachedShopPage = new PageNavigationEntry_ShopPage.WithParent(this._value.shopPage, this._parent && this._parent.entryCase === 16 ? this._parent.shopPage : null);
      } else {
        this._cachedShopPage = PageNavigationEntry_ShopPage.WithParent.default;
      }
    }
    return this._cachedShopPage;
  }
  get cardListPage() {
    if (!this._cachedCardListPage) {
      if (this._value._entryCase === 0) {
        this._cachedCardListPage = this._parent ? new PageNavigationEntry_CardListPage.WithParent(PageNavigationEntry_CardListPage.default, this._parent.cardListPage) : this._value.cardListPage;
      } else if (this._value._entryCase === 17) {
        this._cachedCardListPage = new PageNavigationEntry_CardListPage.WithParent(this._value.cardListPage, this._parent && this._parent.entryCase === 17 ? this._parent.cardListPage : null);
      } else {
        this._cachedCardListPage = PageNavigationEntry_CardListPage.WithParent.default;
      }
    }
    return this._cachedCardListPage;
  }
  get campaignPage() {
    if (!this._cachedCampaignPage) {
      if (this._value._entryCase === 0) {
        this._cachedCampaignPage = this._parent ? new PageNavigationEntry_CampaignPage.WithParent(PageNavigationEntry_CampaignPage.default, this._parent.campaignPage) : this._value.campaignPage;
      } else if (this._value._entryCase === 18) {
        this._cachedCampaignPage = new PageNavigationEntry_CampaignPage.WithParent(this._value.campaignPage, this._parent && this._parent.entryCase === 18 ? this._parent.campaignPage : null);
      } else {
        this._cachedCampaignPage = PageNavigationEntry_CampaignPage.WithParent.default;
      }
    }
    return this._cachedCampaignPage;
  }
  get facebookPage() {
    if (!this._cachedFacebookPage) {
      if (this._value._entryCase === 0) {
        this._cachedFacebookPage = this._parent ? new PageNavigationEntry_FacebookPage.WithParent(PageNavigationEntry_FacebookPage.default, this._parent.facebookPage) : this._value.facebookPage;
      } else if (this._value._entryCase === 19) {
        this._cachedFacebookPage = new PageNavigationEntry_FacebookPage.WithParent(this._value.facebookPage, this._parent && this._parent.entryCase === 19 ? this._parent.facebookPage : null);
      } else {
        this._cachedFacebookPage = PageNavigationEntry_FacebookPage.WithParent.default;
      }
    }
    return this._cachedFacebookPage;
  }
  get profilePage() {
    if (!this._cachedProfilePage) {
      if (this._value._entryCase === 0) {
        this._cachedProfilePage = this._parent ? new PageNavigationEntry_ProfilePage.WithParent(PageNavigationEntry_ProfilePage.default, this._parent.profilePage) : this._value.profilePage;
      } else if (this._value._entryCase === 20) {
        this._cachedProfilePage = new PageNavigationEntry_ProfilePage.WithParent(this._value.profilePage, this._parent && this._parent.entryCase === 20 ? this._parent.profilePage : null);
      } else {
        this._cachedProfilePage = PageNavigationEntry_ProfilePage.WithParent.default;
      }
    }
    return this._cachedProfilePage;
  }
  get website() {
    if (!this._cachedWebsite) {
      if (this._value._entryCase === 0) {
        this._cachedWebsite = this._parent ? new PageNavigationEntry_Website.WithParent(PageNavigationEntry_Website.default, this._parent.website) : this._value.website;
      } else if (this._value._entryCase === 21) {
        this._cachedWebsite = new PageNavigationEntry_Website.WithParent(this._value.website, this._parent && this._parent.entryCase === 21 ? this._parent.website : null);
      } else {
        this._cachedWebsite = PageNavigationEntry_Website.WithParent.default;
      }
    }
    return this._cachedWebsite;
  }
  get questPage() {
    if (!this._cachedQuestPage) {
      if (this._value._entryCase === 0) {
        this._cachedQuestPage = this._parent ? new PageNavigationEntry_QuestPage.WithParent(PageNavigationEntry_QuestPage.default, this._parent.questPage) : this._value.questPage;
      } else if (this._value._entryCase === 22) {
        this._cachedQuestPage = new PageNavigationEntry_QuestPage.WithParent(this._value.questPage, this._parent && this._parent.entryCase === 22 ? this._parent.questPage : null);
      } else {
        this._cachedQuestPage = PageNavigationEntry_QuestPage.WithParent.default;
      }
    }
    return this._cachedQuestPage;
  }
  get tournamentPage() {
    if (!this._cachedTournamentPage) {
      if (this._value._entryCase === 0) {
        this._cachedTournamentPage = this._parent ? new PageNavigationEntry_TournamentPage.WithParent(PageNavigationEntry_TournamentPage.default, this._parent.tournamentPage) : this._value.tournamentPage;
      } else if (this._value._entryCase === 23) {
        this._cachedTournamentPage = new PageNavigationEntry_TournamentPage.WithParent(this._value.tournamentPage, this._parent && this._parent.entryCase === 23 ? this._parent.tournamentPage : null);
      } else {
        this._cachedTournamentPage = PageNavigationEntry_TournamentPage.WithParent.default;
      }
    }
    return this._cachedTournamentPage;
  }
  get eventPage() {
    if (!this._cachedEventPage) {
      if (this._value._entryCase === 0) {
        this._cachedEventPage = this._parent ? new PageNavigationEntry_EventPage.WithParent(PageNavigationEntry_EventPage.default, this._parent.eventPage) : this._value.eventPage;
      } else if (this._value._entryCase === 24) {
        this._cachedEventPage = new PageNavigationEntry_EventPage.WithParent(this._value.eventPage, this._parent && this._parent.entryCase === 24 ? this._parent.eventPage : null);
      } else {
        this._cachedEventPage = PageNavigationEntry_EventPage.WithParent.default;
      }
    }
    return this._cachedEventPage;
  }
  get guildPage() {
    if (!this._cachedGuildPage) {
      if (this._value._entryCase === 0) {
        this._cachedGuildPage = this._parent ? new PageNavigationEntry_GuildPage.WithParent(PageNavigationEntry_GuildPage.default, this._parent.guildPage) : this._value.guildPage;
      } else if (this._value._entryCase === 25) {
        this._cachedGuildPage = new PageNavigationEntry_GuildPage.WithParent(this._value.guildPage, this._parent && this._parent.entryCase === 25 ? this._parent.guildPage : null);
      } else {
        this._cachedGuildPage = PageNavigationEntry_GuildPage.WithParent.default;
      }
    }
    return this._cachedGuildPage;
  }
  get loadoutPage() {
    if (!this._cachedLoadoutPage) {
      if (this._value._entryCase === 0) {
        this._cachedLoadoutPage = this._parent ? new PageNavigationEntry_LoadoutPage.WithParent(PageNavigationEntry_LoadoutPage.default, this._parent.loadoutPage) : this._value.loadoutPage;
      } else if (this._value._entryCase === 26) {
        this._cachedLoadoutPage = new PageNavigationEntry_LoadoutPage.WithParent(this._value.loadoutPage, this._parent && this._parent.entryCase === 26 ? this._parent.loadoutPage : null);
      } else {
        this._cachedLoadoutPage = PageNavigationEntry_LoadoutPage.WithParent.default;
      }
    }
    return this._cachedLoadoutPage;
  }
  get settingPage() {
    if (!this._cachedSettingPage) {
      if (this._value._entryCase === 0) {
        this._cachedSettingPage = this._parent ? new PageNavigationEntry_SettingPage.WithParent(PageNavigationEntry_SettingPage.default, this._parent.settingPage) : this._value.settingPage;
      } else if (this._value._entryCase === 27) {
        this._cachedSettingPage = new PageNavigationEntry_SettingPage.WithParent(this._value.settingPage, this._parent && this._parent.entryCase === 27 ? this._parent.settingPage : null);
      } else {
        this._cachedSettingPage = PageNavigationEntry_SettingPage.WithParent.default;
      }
    }
    return this._cachedSettingPage;
  }
  get friendPage() {
    if (!this._cachedFriendPage) {
      if (this._value._entryCase === 0) {
        this._cachedFriendPage = this._parent ? new PageNavigationEntry_FriendPage.WithParent(PageNavigationEntry_FriendPage.default, this._parent.friendPage) : this._value.friendPage;
      } else if (this._value._entryCase === 28) {
        this._cachedFriendPage = new PageNavigationEntry_FriendPage.WithParent(this._value.friendPage, this._parent && this._parent.entryCase === 28 ? this._parent.friendPage : null);
      } else {
        this._cachedFriendPage = PageNavigationEntry_FriendPage.WithParent.default;
      }
    }
    return this._cachedFriendPage;
  }
  get itemNavigationPage() {
    if (!this._cachedItemNavigationPage) {
      if (this._value._entryCase === 0) {
        this._cachedItemNavigationPage = this._parent ? new PageNavigationEntry_ItemNavigationPage.WithParent(PageNavigationEntry_ItemNavigationPage.default, this._parent.itemNavigationPage) : this._value.itemNavigationPage;
      } else if (this._value._entryCase === 29) {
        this._cachedItemNavigationPage = new PageNavigationEntry_ItemNavigationPage.WithParent(this._value.itemNavigationPage, this._parent && this._parent.entryCase === 29 ? this._parent.itemNavigationPage : null);
      } else {
        this._cachedItemNavigationPage = PageNavigationEntry_ItemNavigationPage.WithParent.default;
      }
    }
    return this._cachedItemNavigationPage;
  }
  update(current, parent) {
    const result = new PageNavigationEntry_WithParent(current, parent);
    if (this._cachedHeroCardPage && this._value._entryCase === 11 && current._entryCase === 11) {
      if (this._value.heroCardPage === current.heroCardPage && this._parent === parent) {
        result._cachedHeroCardPage = this._cachedHeroCardPage;
      } else {
        result._cachedHeroCardPage = this._cachedHeroCardPage.update(current.heroCardPage, parent && parent.entryCase === 11 ? parent.heroCardPage : null);
      }
    }
    if (this._cachedCranePage && this._value._entryCase === 12 && current._entryCase === 12) {
      if (this._value.cranePage === current.cranePage && this._parent === parent) {
        result._cachedCranePage = this._cachedCranePage;
      } else {
        result._cachedCranePage = this._cachedCranePage.update(current.cranePage, parent && parent.entryCase === 12 ? parent.cranePage : null);
      }
    }
    if (this._cachedPortalPage && this._value._entryCase === 13 && current._entryCase === 13) {
      if (this._value.portalPage === current.portalPage && this._parent === parent) {
        result._cachedPortalPage = this._cachedPortalPage;
      } else {
        result._cachedPortalPage = this._cachedPortalPage.update(current.portalPage, parent && parent.entryCase === 13 ? parent.portalPage : null);
      }
    }
    if (this._cachedMinePage && this._value._entryCase === 14 && current._entryCase === 14) {
      if (this._value.minePage === current.minePage && this._parent === parent) {
        result._cachedMinePage = this._cachedMinePage;
      } else {
        result._cachedMinePage = this._cachedMinePage.update(current.minePage, parent && parent.entryCase === 14 ? parent.minePage : null);
      }
    }
    if (this._cachedMarketPage && this._value._entryCase === 15 && current._entryCase === 15) {
      if (this._value.marketPage === current.marketPage && this._parent === parent) {
        result._cachedMarketPage = this._cachedMarketPage;
      } else {
        result._cachedMarketPage = this._cachedMarketPage.update(current.marketPage, parent && parent.entryCase === 15 ? parent.marketPage : null);
      }
    }
    if (this._cachedShopPage && this._value._entryCase === 16 && current._entryCase === 16) {
      if (this._value.shopPage === current.shopPage && this._parent === parent) {
        result._cachedShopPage = this._cachedShopPage;
      } else {
        result._cachedShopPage = this._cachedShopPage.update(current.shopPage, parent && parent.entryCase === 16 ? parent.shopPage : null);
      }
    }
    if (this._cachedCardListPage && this._value._entryCase === 17 && current._entryCase === 17) {
      if (this._value.cardListPage === current.cardListPage && this._parent === parent) {
        result._cachedCardListPage = this._cachedCardListPage;
      } else {
        result._cachedCardListPage = this._cachedCardListPage.update(current.cardListPage, parent && parent.entryCase === 17 ? parent.cardListPage : null);
      }
    }
    if (this._cachedCampaignPage && this._value._entryCase === 18 && current._entryCase === 18) {
      if (this._value.campaignPage === current.campaignPage && this._parent === parent) {
        result._cachedCampaignPage = this._cachedCampaignPage;
      } else {
        result._cachedCampaignPage = this._cachedCampaignPage.update(current.campaignPage, parent && parent.entryCase === 18 ? parent.campaignPage : null);
      }
    }
    if (this._cachedFacebookPage && this._value._entryCase === 19 && current._entryCase === 19) {
      if (this._value.facebookPage === current.facebookPage && this._parent === parent) {
        result._cachedFacebookPage = this._cachedFacebookPage;
      } else {
        result._cachedFacebookPage = this._cachedFacebookPage.update(current.facebookPage, parent && parent.entryCase === 19 ? parent.facebookPage : null);
      }
    }
    if (this._cachedProfilePage && this._value._entryCase === 20 && current._entryCase === 20) {
      if (this._value.profilePage === current.profilePage && this._parent === parent) {
        result._cachedProfilePage = this._cachedProfilePage;
      } else {
        result._cachedProfilePage = this._cachedProfilePage.update(current.profilePage, parent && parent.entryCase === 20 ? parent.profilePage : null);
      }
    }
    if (this._cachedWebsite && this._value._entryCase === 21 && current._entryCase === 21) {
      if (this._value.website === current.website && this._parent === parent) {
        result._cachedWebsite = this._cachedWebsite;
      } else {
        result._cachedWebsite = this._cachedWebsite.update(current.website, parent && parent.entryCase === 21 ? parent.website : null);
      }
    }
    if (this._cachedQuestPage && this._value._entryCase === 22 && current._entryCase === 22) {
      if (this._value.questPage === current.questPage && this._parent === parent) {
        result._cachedQuestPage = this._cachedQuestPage;
      } else {
        result._cachedQuestPage = this._cachedQuestPage.update(current.questPage, parent && parent.entryCase === 22 ? parent.questPage : null);
      }
    }
    if (this._cachedTournamentPage && this._value._entryCase === 23 && current._entryCase === 23) {
      if (this._value.tournamentPage === current.tournamentPage && this._parent === parent) {
        result._cachedTournamentPage = this._cachedTournamentPage;
      } else {
        result._cachedTournamentPage = this._cachedTournamentPage.update(current.tournamentPage, parent && parent.entryCase === 23 ? parent.tournamentPage : null);
      }
    }
    if (this._cachedEventPage && this._value._entryCase === 24 && current._entryCase === 24) {
      if (this._value.eventPage === current.eventPage && this._parent === parent) {
        result._cachedEventPage = this._cachedEventPage;
      } else {
        result._cachedEventPage = this._cachedEventPage.update(current.eventPage, parent && parent.entryCase === 24 ? parent.eventPage : null);
      }
    }
    if (this._cachedGuildPage && this._value._entryCase === 25 && current._entryCase === 25) {
      if (this._value.guildPage === current.guildPage && this._parent === parent) {
        result._cachedGuildPage = this._cachedGuildPage;
      } else {
        result._cachedGuildPage = this._cachedGuildPage.update(current.guildPage, parent && parent.entryCase === 25 ? parent.guildPage : null);
      }
    }
    if (this._cachedLoadoutPage && this._value._entryCase === 26 && current._entryCase === 26) {
      if (this._value.loadoutPage === current.loadoutPage && this._parent === parent) {
        result._cachedLoadoutPage = this._cachedLoadoutPage;
      } else {
        result._cachedLoadoutPage = this._cachedLoadoutPage.update(current.loadoutPage, parent && parent.entryCase === 26 ? parent.loadoutPage : null);
      }
    }
    if (this._cachedSettingPage && this._value._entryCase === 27 && current._entryCase === 27) {
      if (this._value.settingPage === current.settingPage && this._parent === parent) {
        result._cachedSettingPage = this._cachedSettingPage;
      } else {
        result._cachedSettingPage = this._cachedSettingPage.update(current.settingPage, parent && parent.entryCase === 27 ? parent.settingPage : null);
      }
    }
    if (this._cachedFriendPage && this._value._entryCase === 28 && current._entryCase === 28) {
      if (this._value.friendPage === current.friendPage && this._parent === parent) {
        result._cachedFriendPage = this._cachedFriendPage;
      } else {
        result._cachedFriendPage = this._cachedFriendPage.update(current.friendPage, parent && parent.entryCase === 28 ? parent.friendPage : null);
      }
    }
    if (this._cachedItemNavigationPage && this._value._entryCase === 29 && current._entryCase === 29) {
      if (this._value.itemNavigationPage === current.itemNavigationPage && this._parent === parent) {
        result._cachedItemNavigationPage = this._cachedItemNavigationPage;
      } else {
        result._cachedItemNavigationPage = this._cachedItemNavigationPage.update(current.itemNavigationPage, parent && parent.entryCase === 29 ? parent.itemNavigationPage : null);
      }
    }
    return result;
  }
}
PageNavigationEntry_WithParent.default = new PageNavigationEntry_WithParent(PageNavigationEntry.default, null);
PageNavigationEntry.WithParent = PageNavigationEntry_WithParent;
class PageNavigationEntry_Descriptor {
  get heroCardPage() {
    if (!this._heroCardPage) {
      this._heroCardPage = {
        name: "heroCardPage",
        Name: "HeroCardPage",
        number: 11,
        protoType: 11,
        protoName: "hero_card_page",
        oneofIndex: 0,
        type: PageNavigationEntry_HeroCardPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._heroCardPage;
  }
  get cranePage() {
    if (!this._cranePage) {
      this._cranePage = {
        name: "cranePage",
        Name: "CranePage",
        number: 12,
        protoType: 11,
        protoName: "crane_page",
        oneofIndex: 0,
        type: PageNavigationEntry_CranePage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._cranePage;
  }
  get portalPage() {
    if (!this._portalPage) {
      this._portalPage = {
        name: "portalPage",
        Name: "PortalPage",
        number: 13,
        protoType: 11,
        protoName: "portal_page",
        oneofIndex: 0,
        type: PageNavigationEntry_PortalPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._portalPage;
  }
  get minePage() {
    if (!this._minePage) {
      this._minePage = {
        name: "minePage",
        Name: "MinePage",
        number: 14,
        protoType: 11,
        protoName: "mine_page",
        oneofIndex: 0,
        type: PageNavigationEntry_MinePage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._minePage;
  }
  get marketPage() {
    if (!this._marketPage) {
      this._marketPage = {
        name: "marketPage",
        Name: "MarketPage",
        number: 15,
        protoType: 11,
        protoName: "market_page",
        oneofIndex: 0,
        type: PageNavigationEntry_MarketPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._marketPage;
  }
  get shopPage() {
    if (!this._shopPage) {
      this._shopPage = {
        name: "shopPage",
        Name: "ShopPage",
        number: 16,
        protoType: 11,
        protoName: "shop_page",
        oneofIndex: 0,
        type: PageNavigationEntry_ShopPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._shopPage;
  }
  get cardListPage() {
    if (!this._cardListPage) {
      this._cardListPage = {
        name: "cardListPage",
        Name: "CardListPage",
        number: 17,
        protoType: 11,
        protoName: "card_list_page",
        oneofIndex: 0,
        type: PageNavigationEntry_CardListPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._cardListPage;
  }
  get campaignPage() {
    if (!this._campaignPage) {
      this._campaignPage = {
        name: "campaignPage",
        Name: "CampaignPage",
        number: 18,
        protoType: 11,
        protoName: "campaign_page",
        oneofIndex: 0,
        type: PageNavigationEntry_CampaignPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._campaignPage;
  }
  get facebookPage() {
    if (!this._facebookPage) {
      this._facebookPage = {
        name: "facebookPage",
        Name: "FacebookPage",
        number: 19,
        protoType: 11,
        protoName: "facebook_page",
        oneofIndex: 0,
        type: PageNavigationEntry_FacebookPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._facebookPage;
  }
  get profilePage() {
    if (!this._profilePage) {
      this._profilePage = {
        name: "profilePage",
        Name: "ProfilePage",
        number: 20,
        protoType: 11,
        protoName: "profile_page",
        oneofIndex: 0,
        type: PageNavigationEntry_ProfilePage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._profilePage;
  }
  get website() {
    if (!this._website) {
      this._website = {
        name: "website",
        Name: "Website",
        number: 21,
        protoType: 11,
        protoName: "website",
        oneofIndex: 0,
        type: PageNavigationEntry_Website,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._website;
  }
  get questPage() {
    if (!this._questPage) {
      this._questPage = {
        name: "questPage",
        Name: "QuestPage",
        number: 22,
        protoType: 11,
        protoName: "quest_page",
        oneofIndex: 0,
        type: PageNavigationEntry_QuestPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._questPage;
  }
  get tournamentPage() {
    if (!this._tournamentPage) {
      this._tournamentPage = {
        name: "tournamentPage",
        Name: "TournamentPage",
        number: 23,
        protoType: 11,
        protoName: "tournament_page",
        oneofIndex: 0,
        type: PageNavigationEntry_TournamentPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._tournamentPage;
  }
  get eventPage() {
    if (!this._eventPage) {
      this._eventPage = {
        name: "eventPage",
        Name: "EventPage",
        number: 24,
        protoType: 11,
        protoName: "event_page",
        oneofIndex: 0,
        type: PageNavigationEntry_EventPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._eventPage;
  }
  get guildPage() {
    if (!this._guildPage) {
      this._guildPage = {
        name: "guildPage",
        Name: "GuildPage",
        number: 25,
        protoType: 11,
        protoName: "guild_page",
        oneofIndex: 0,
        type: PageNavigationEntry_GuildPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._guildPage;
  }
  get loadoutPage() {
    if (!this._loadoutPage) {
      this._loadoutPage = {
        name: "loadoutPage",
        Name: "LoadoutPage",
        number: 26,
        protoType: 11,
        protoName: "loadout_page",
        oneofIndex: 0,
        type: PageNavigationEntry_LoadoutPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._loadoutPage;
  }
  get settingPage() {
    if (!this._settingPage) {
      this._settingPage = {
        name: "settingPage",
        Name: "SettingPage",
        number: 27,
        protoType: 11,
        protoName: "setting_page",
        oneofIndex: 0,
        type: PageNavigationEntry_SettingPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._settingPage;
  }
  get friendPage() {
    if (!this._friendPage) {
      this._friendPage = {
        name: "friendPage",
        Name: "FriendPage",
        number: 28,
        protoType: 11,
        protoName: "friend_page",
        oneofIndex: 0,
        type: PageNavigationEntry_FriendPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._friendPage;
  }
  get itemNavigationPage() {
    if (!this._itemNavigationPage) {
      this._itemNavigationPage = {
        name: "itemNavigationPage",
        Name: "ItemNavigationPage",
        number: 29,
        protoType: 11,
        protoName: "item_navigation_page",
        oneofIndex: 0,
        type: PageNavigationEntry_ItemNavigationPage,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._itemNavigationPage;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.heroCardPage,
        this.cranePage,
        this.portalPage,
        this.minePage,
        this.marketPage,
        this.shopPage,
        this.cardListPage,
        this.campaignPage,
        this.facebookPage,
        this.profilePage,
        this.website,
        this.questPage,
        this.tournamentPage,
        this.eventPage,
        this.guildPage,
        this.loadoutPage,
        this.settingPage,
        this.friendPage,
        this.itemNavigationPage,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "entry",
        Name: "Entry",
        fields: [
          this.fields[0],
          this.fields[1],
          this.fields[2],
          this.fields[3],
          this.fields[4],
          this.fields[5],
          this.fields[6],
          this.fields[7],
          this.fields[8],
          this.fields[9],
          this.fields[10],
          this.fields[11],
          this.fields[12],
          this.fields[13],
          this.fields[14],
          this.fields[15],
          this.fields[16],
          this.fields[17],
          this.fields[18],
        ],
      });
    }
    return this._oneofs;
  }
}
PageNavigationEntry.descriptor = new PageNavigationEntry_Descriptor();
